/**
 * Newton promotion engine foundation.
 *
 * This module owns only promo metadata and promo gift reconciliation. It does
 * not replace the theme's existing chair, warranty, or bundle cart systems.
 */
(() => {
  const configNode = document.getElementById('promo-engine-config');
  if (!configNode || configNode.dataset.enabled !== 'true') return;

  // The bootstrap snippet anchors this clock to Shopify server time and
  // interprets offset-less campaign values in Asia/Jerusalem. Keep a small
  // fallback for cached/older pages where that bootstrap is not present yet.
  const promoTime = window.__promoTime || {
    now: () => Date.now(),
    parseCampaignEnd: (value) => Date.parse(String(value || '')),
  };

  const config = {
    campaign: configNode.dataset.campaign || 'off',
    couponStackingEnabled: configNode.dataset.couponStackingEnabled === 'true',
    maxUnits: Math.max(1, Number(configNode.dataset.maxUnits || 999)),
    cartUrl: '/cart.js',
    addUrl: '/cart/add.js',
    changeUrl: '/cart/change.js',
    updateUrl: '/cart/update.js',
  };

  function initPromoCountdowns() {
    document.querySelectorAll('[data-promo-countdown]').forEach((node) => {
      const end = promoTime.parseCampaignEnd(node.dataset.promoCountdownEnd || '');
      const clock = node.querySelector('[data-promo-countdown-clock]');
      if (!Number.isFinite(end) || !clock) { node.hidden = true; return; }
      const render = () => {
        const remaining = end - promoTime.now();
        if (remaining <= 0) { node.hidden = true; return; }
        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        clock.innerHTML = [
          [days, 'ימים'], [hours, 'שעות'], [minutes, 'דק׳'], [seconds, 'שנ׳'],
        ].map(([value, label]) => `<span class="promo-countdown__unit"><b>${String(value).padStart(2, '0')}</b><small>${label}</small></span>`).join('');
      };
      render();
      window.setInterval(render, 1000);
    });
  }

  let productContext = null;
  const readProductContext = () => {
    const productContextNode = document.getElementById('promo-product-context');
    if (!productContextNode) {
      productContext = null;
      window.__promoSelectedVariantId = 0;
      window.__promoProductContext = null;
      return;
    }
    try {
      productContext = JSON.parse(productContextNode.textContent || '{}');
      // Product/color switchers replace the product section but not the
      // layout-level promo JSON. The summary block carries a fresh compact
      // context with the selected product's exact variant references; merge
      // it before exposing the context to forms, summaries, and reconciliation.
      const sectionContextNode = document.querySelector('[data-promo-section-context]');
      if (sectionContextNode) {
        try {
          const sectionContext = JSON.parse(sectionContextNode.textContent || '{}');
          if (sectionContext && Number(sectionContext.productId || 0) > 0) {
            const existingMap = productContext.fixedVariantsByVariant && typeof productContext.fixedVariantsByVariant === 'object'
              ? productContext.fixedVariantsByVariant
              : {};
            productContext = { ...productContext, ...sectionContext };
            if (Array.isArray(sectionContext.fixedVariants) && sectionContext.variantId) {
              productContext.fixedVariantsByVariant = {
                ...existingMap,
                [String(sectionContext.variantId)]: sectionContext.fixedVariants,
              };
            }
          }
        } catch (error) {
          console.warn('[Promo] Invalid section product context', error);
        }
      }
      // Keep one authoritative selected variant marker while the colour
      // switcher replaces the product section. The new section's hidden form
      // can briefly contain the previous colour during that replacement.
      window.__promoSelectedVariantId = Number(productContext.variantId || 0);
      // Customer-choice gifts are defined only for Omni Pro and Maxis. A
      // stale metafield on another chair must never reopen the choice modal.
      const modelText = [productContext.chairModel, productContext.handle].filter(Boolean).join(' ').toLowerCase();
      if (!/omni\s+pro|maxis/.test(modelText)) productContext.choiceEnabled = false;
      window.__promoProductContext = productContext;
    } catch (error) {
      productContext = null;
      window.__promoSelectedVariantId = 0;
      window.__promoProductContext = null;
      console.warn('[Promo] Invalid product context', error);
    }
  };
  readProductContext();

  const urlVariantId = () => {
    try { return new URL(window.location.href).searchParams.get('variant') || ''; } catch (_) { return ''; }
  };

  const applyPromoVariantContext = (variantId) => {
    const numericVariantId = Number(variantId || 0);
    if (!productContext || !Number.isFinite(numericVariantId) || numericVariantId <= 0) return;
    productContext.variantId = numericVariantId;
    window.__promoSelectedVariantId = numericVariantId;
    const variantMap = productContext.fixedVariantsByVariant;
    const hasVariantEntry = variantMap && typeof variantMap === 'object'
      && Object.prototype.hasOwnProperty.call(variantMap, String(numericVariantId));
    const variantGifts = hasVariantEntry ? variantMap[String(numericVariantId)] : null;
    if (hasVariantEntry && Array.isArray(variantGifts)) {
      productContext.fixedVariants = variantGifts;
      productContext.fixedVariantMode = variantGifts.length > 0;
    }
    window.__promoProductContext = productContext;
  };

  const inferVisibleChairColor = (detail = {}) => {
    const direct = detail.color || detail.data?.color || detail.resource?.color || '';
    if (direct) return String(direct).trim();
    const knownColors = /glacier|graphite|obsidian|midnight black|space grey|space gray|moss green/i;
    const checked = [...document.querySelectorAll('input[type="radio"]:checked')]
      .map((input) => input.value || input.dataset.color || '')
      .find((value) => knownColors.test(String(value)));
    if (checked) return String(checked).trim();
    const active = document.querySelector('[data-color-name].is-active, [data-color].is-active');
    const activeColor = active?.dataset.colorName || active?.dataset.color || '';
    return String(activeColor || window.currentProductColor || document.documentElement.dataset.currentProductColor || '').trim();
  };

  const syncPromoProductVariant = (detail = {}) => {
    if (!productContext) return;
    // Some section-swap events do not carry a variant id. The URL is updated
    // by the colour switcher before those events fire, so use it to prevent
    // an old layout context from restoring the previous colour's gifts.
    const variantId = detail.variantId || detail.id || detail.resource?.id || detail.data?.variantId || detail.data?.resource?.id || urlVariantId();
    if (variantId) {
      // Variant-level exact gift references must follow the chair colour
      // switch. The Liquid context contains a map for every chair variant so
      // no product/colour guessing is needed after a swatch change.
      applyPromoVariantContext(variantId);
    }
    const color = inferVisibleChairColor(detail);
    if (color) productContext.chairColor = color;
    window.__promoProductContext = productContext;
    document.dispatchEvent(new CustomEvent('promo:context-updated'));
  };

  // Color swatches and the native variant picker emit different events. Keep
  // the promo context in sync with either path so the preview image and the
  // actual cart gift use the newly selected chair color.
  document.addEventListener('variant:change', (event) => syncPromoProductVariant(event.detail || {}));
  document.addEventListener('variant:update', (event) => syncPromoProductVariant(event.detail || {}));
  document.addEventListener('change', (event) => {
    if (event.target?.closest?.('variant-picker, .product-details, form')) {
      window.setTimeout(() => syncPromoProductVariant({}), 0);
    }
  });

  const clean = (value) => String(value ?? '').replace(/[\[\]{}"'`]/g, '').trim();
  const normalize = (value) => clean(value).toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const colorKey = (value) => {
    const text = normalize(value);
    if (text.includes('obsidian')) return 'obsidian';
    if (text.includes('graphite')) return 'graphite';
    if (text.includes('glacier')) return 'glacier';
    if (text.includes('midnight black') || text === 'black') return 'black';
    if (text.includes('space grey') || text.includes('space gray') || text === 'grey' || text === 'gray') return 'grey';
    if (text.includes('moss green') || text.includes('moss') || text === 'green') return 'green';
    return text;
  };
  const modelKey = (value) => {
    const text = normalize(value);
    if (text.includes('omni gen')) return 'omni gen';
    if (text.includes('omni se')) return 'omni se';
    if (text.includes('omni pro')) return 'omni pro';
    if (text.includes('maxis manual') || text === 'manual') return 'maxis manual';
    if (text.includes('maxis electric') || text === 'electric') return 'maxis electric';
    if (text.includes('maxis airflow') || text === 'airflow') return 'maxis airflow';
    // Some products expose only the generic Maxis value. Keep it as a
    // fallback, but never let it override an explicit Manual/Electric/Airflow
    // model detected above.
    if (text === 'maxis' || text.includes('libernovo maxis')) return 'maxis';
    if (text === 'omni' || text.includes('libernovo omni')) return 'omni';
    // Newton chairs receive the old Omni footrest group.
    return 'omni';
  };
  const choiceModelAllowed = (value) => /omni\s+pro|maxis/i.test(String(value || ''));
  const splitTokens = (value) => clean(value)
    .split(/\s*(?:\/|\+|,|&)\s*/g)
    .map(modelKey)
    .filter(Boolean);
  const compatibleModels = (model) => {
    const key = modelKey(model);
    return [key];
  };
  const optionIndex = (product, aliases) => {
    const names = Array.isArray(product?.optionNames) ? product.optionNames.map(normalize) : [];
    const wanted = aliases.map(normalize);
    return names.findIndex((name) => wanted.some((alias) => name === alias || name.includes(alias) || alias.includes(name)));
  };
  const variantOption = (product, variant, aliases) => {
    const index = optionIndex(product, aliases);
    if (index < 0) return '';
    return clean([variant.option1, variant.option2, variant.option3][index]);
  };
  const resolvePromoVariant = (product, chairModel, chairColor, compatibilityModel = chairModel) => {
    if (!product?.variants?.length) return null;
    const targetModels = compatibleModels(compatibilityModel);
    const targetColor = colorKey(chairColor);
    const scored = product.variants.map((variant) => {
      const variantColor = colorKey(variantOption(product, variant, ['Color', 'Colour', 'צבע']) || variant.title);
      const compatibility = variantOption(product, variant, [
        'Compatible Chair Group', 'Compatible Group', 'Compatible Chair Model',
        'Compatible Model', 'Chair Model', 'Model'
      ]);
      const tokens = splitTokens(compatibility);
      const matchedModel = targetModels.find((model) => tokens.includes(model));
      if (compatibility && !matchedModel) return null;
      let score = 0;
      if (targetColor && variantColor === targetColor) score += 100;
      // If the exact chair color does not exist for this compatible group,
      // keep the compatible available variant instead of dropping the gift.
      if (matchedModel) score += 50;
      if (variant.available) score += 10;
      return { variant, score };
    }).filter(Boolean).sort((a, b) => b.score - a.score);
    if (scored[0]?.variant) return scored[0].variant;

    // Keep a current-generation gift attached if a store has a legacy or
    // incomplete compatibility value on one variant. Color is still used
    // when possible, then the first available variant is used safely.
    const fallback = product.variants.map((variant) => {
      const variantColor = colorKey(variantOption(product, variant, ['Color', 'Colour', 'צבע']) || variant.title);
      let score = variant.available ? 10 : 0;
      if (targetColor && variantColor === targetColor) score += 100;
      return { variant, score };
    }).sort((a, b) => b.score - a.score)[0];
    return fallback?.variant || null;
  };

  const resolveOldOmniBatteryVariant = (product, chairColor) => {
    if (!product?.variants?.length) return null;
    const targetColor = colorKey(chairColor);
    const oldOmniVariants = product.variants.filter((variant) => {
      const compatibility = variantOption(product, variant, [
        'Compatible Chair Group', 'Compatible Group', 'Compatible Chair Model',
        'Compatible Model', 'Chair Model', 'Model'
      ]);
      if (!compatibility) return false;
      const tokens = splitTokens(compatibility);
      return tokens.includes('omni') && !tokens.includes('omni gen')
        && !tokens.includes('omni se') && !tokens.includes('omni pro');
    });
    // The old-stock group is explicitly “Omni”. Never let a same-colour
    // Omni Gen/Pro battery win over it when the chair is Glacier/Graphite.
    const candidates = oldOmniVariants.length ? oldOmniVariants : product.variants;
    const scored = candidates.map((variant) => {
      const variantColor = colorKey(variantOption(product, variant, ['Color', 'Colour', 'צבע']) || variant.title);
      const compatibility = variantOption(product, variant, [
        'Compatible Chair Group', 'Compatible Group', 'Compatible Chair Model',
        'Compatible Model', 'Chair Model', 'Model'
      ]);
      const tokens = splitTokens(compatibility);
      const capacity = normalize(variantOption(product, variant, ['Capacity', 'Battery Capacity', 'mAh']));
      // Old Omni battery stock is the 2200mAh group. Some catalogues label
      // that group as Omni, others as Omni Gen, so accept both labels while
      // preferring an explicit Omni/2200mAh variant.
      if (oldOmniVariants.length && (!tokens.includes('omni') || tokens.includes('omni gen'))) return null;
      let score = variant.available ? 10 : 0;
      if (targetColor && variantColor === targetColor) score += 100;
      if (tokens.includes('omni')) score += 200;
      if (capacity.includes('2200')) score += 30;
      return { variant, score };
    }).filter(Boolean).sort((a, b) => b.score - a.score);
    const matching = scored.find(({ variant }) => targetColor && colorKey(variantOption(product, variant, ['Color', 'Colour', 'צבע']) || variant.title) === targetColor);
    if (matching) return matching.variant;
    const midnight = scored.find(({ variant }) => colorKey(variantOption(product, variant, ['Color', 'Colour', 'צבע']) || variant.title) === 'black');
    return midnight?.variant || scored[0]?.variant || product.variants.find((variant) => variant.available) || product.variants[0] || null;
  };

  function giftDisplayMeta(product, variant, chairModel, displayModelOverride = '') {
    if (!variant) return null;
    const rawModel = variantOption(product, variant, [
      'Compatible Chair Group', 'Compatible Group', 'Compatible Chair Model',
      'Compatible Model', 'Chair Model', 'Model'
    ]) || '';
    const modelTokens = clean(rawModel)
      .split(/\s*(?:\/|\+|,|&)\s*/g)
      .map((token) => token.trim())
      .filter(Boolean);
    const chairKey = modelKey(chairModel);
    const displayModel = displayModelOverride || modelTokens.find((token) => modelKey(token) === chairKey)
      || (chairKey === 'omni se' ? modelTokens.find((token) => modelKey(token) === 'omni gen') : '')
      || modelTokens[0]
      || rawModel;
    return {
      id: Number(variant.id),
      productTitle: product.title || '',
      title: variant.title || '',
      model: displayModel,
      color: variantOption(product, variant, ['Color', 'Colour', 'צבע']) || '',
      image: variant.image || '',
    };
  }

  // Exact gift references are configured per chair variant, but an older
  // product-level configuration can still leave a second automatic Footrest
  // or Battery in the same bundle. A customer choice may itself be a
  // Footrest, so only collapse *automatic* gifts here. This keeps one
  // canonical automatic item of each type per chair while preserving the
  // separately selected choice gift.
  function automaticGiftKind(gift) {
    if (!gift || gift.kind !== 'auto') return '';
    const text = normalize([
      gift.productTitle,
      gift.title,
      gift.variantTitle,
      gift.handle,
    ].filter(Boolean).join(' '));
    if (/battery|batter/.test(text)) return 'battery';
    if (/footrest|foot support|foot-support/.test(text)) return 'footrest';
    if (/mouse\s*pad|mousepad/.test(text)) return 'mousepad';
    return '';
  }

  function automaticGiftScore(gift, chairModel, chairColor) {
    const text = normalize([
      gift?.productTitle,
      gift?.title,
      gift?.variantTitle,
      gift?.model,
      gift?.color,
    ].filter(Boolean).join(' '));
    const model = modelKey(chairModel);
    const color = colorKey(chairColor);
    let score = 0;
    if (model && text.includes(model)) score += 200;
    if (color && text.includes(color)) score += 100;
    if (gift?.available !== false) score += 10;
    return score;
  }

  function canonicalAutomaticGifts(details, chairModel, chairColor) {
    const winners = new Map();
    const output = [];
    details.filter(Boolean).forEach((gift) => {
      const kind = automaticGiftKind(gift);
      if (!kind) {
        output.push(gift);
        return;
      }
      const existing = winners.get(kind);
      if (!existing) {
        const record = { gift, index: output.length, score: automaticGiftScore(gift, chairModel, chairColor) };
        winners.set(kind, record);
        output.push(gift);
        return;
      }
      const score = automaticGiftScore(gift, chairModel, chairColor);
      if (score > existing.score) {
        output[existing.index] = gift;
        existing.gift = gift;
        existing.score = score;
      }
    });
    return output;
  }

  function resolvedGiftDetails(context) {
    if (!context) return [];
    const model = modelKey([context.chairModel, context.handle].filter(Boolean).join(' '));
    // The swapped product JSON is authoritative. The global swatch value is
    // updated just after the swap event and can briefly still contain the
    // previous chair colour.
    const color = context.chairColor || window.currentProductColor || '';
    const details = [];
    const exactFixedEntries = Array.isArray(context.fixedVariants) ? context.fixedVariants : [];
    // Treat the exact-reference mode as active only when the references were
    // actually serialized. This keeps the legacy resolver available if a
    // malformed/empty metafield value reaches the page.
    const exactFixedMode = exactFixedEntries.length > 0;

    // When exact variant references are configured, they are the complete
    // source of truth. Do not run the legacy model/colour resolver as well,
    // otherwise it can add a second Footrest/Battery variant.
    if (!exactFixedMode) {
      // The promotion table's “current gen” means the matching chair model,
      // not specifically the Omni Gen model. Resolve Omni SE, Omni Pro and
      // each Maxis type against its own compatibility group as a migration
      // fallback for products that have not been configured yet.
      const footrestModel = model;
      let footrest = resolvePromoVariant(context.accessories?.footrest, model, color, footrestModel);
      const modelTitlePatterns = {
        'omni gen': /omni gen/,
        'omni se': /omni se/,
        'omni pro': /omni pro/,
        'maxis manual': /maxis manual/,
        'maxis electric': /maxis electric/,
        'maxis airflow': /maxis airflow/,
      };
      const modelPattern = modelTitlePatterns[model];
      if (modelPattern && context.accessories?.footrest?.variants?.length) {
        const targetColor = colorKey(color);
        const currentGenFootrest = context.accessories.footrest.variants
          .filter((variant) => {
            const title = normalize(variant.title || '');
            const titleColor = colorKey(variant.title || '');
            return (!targetColor || titleColor === targetColor)
              && modelPattern.test(title);
          })
          .sort((a, b) => Number(Boolean(b.available)) - Number(Boolean(a.available)))[0];
        if (currentGenFootrest) footrest = currentGenFootrest;
      }
      if (footrest?.id) details.push(giftDisplayMeta(context.accessories.footrest, footrest, footrestModel));

      // The promotion spec gives the old Omni battery only to Omni Gen.
      if (model === 'omni gen') {
        const battery = resolveOldOmniBatteryVariant(context.accessories?.battery, color);
        if (battery?.id) details.push(giftDisplayMeta(context.accessories.battery, battery, model, 'Omni'));
      }
    }

    // Exact variant references take precedence over the legacy product and
    // handle lists. Each selected variant is passed through unchanged.
    const fixedEntries = exactFixedMode
      ? exactFixedEntries
      : [
        ...(Array.isArray(context.fixedProducts) ? context.fixedProducts : []),
        ...(Array.isArray(context.fixedGifts) ? context.fixedGifts : []),
      ];
    fixedEntries.forEach((gift) => {
      if (gift?.available && gift.variantId && !details.some((item) => item.id === Number(gift.variantId))) {
        details.push({
          id: Number(gift.variantId),
          productTitle: gift.title || '',
          title: gift.title || '',
          variantTitle: gift.variantTitle || '',
          model: '',
          color: gift.color || '',
          image: gift.image || '',
          kind: 'auto',
        });
      }
    });
    if (Number(context.warrantyMonths || 0) > 0 && context.warranty?.available && context.warranty?.variantId
      && !details.some((item) => item.id === Number(context.warranty.variantId))) {
      details.push({
        id: Number(context.warranty.variantId),
        productTitle: context.warranty.title || '',
        title: context.warranty.title || '',
        variantTitle: context.warranty.variantTitle || '',
        model: '',
        color: '',
        image: context.warranty.image || '',
        kind: 'warranty',
      });
    }
    // A customer may choose an accessory from the summary before adding the
    // chair. Keep that choice out of the cart for now, but include the exact
    // selected variant in the chair's promo bundle when the chair is added.
    const pendingChoice = context.choiceEnabled ? readPendingChoice() : null;
    if (pendingChoice?.variantId && !details.some((item) => item.id === Number(pendingChoice.variantId))) {
      details.push({
        id: Number(pendingChoice.variantId),
        productTitle: pendingChoice.productTitle || pendingChoice.title || '',
        title: pendingChoice.title || '',
        variantTitle: pendingChoice.variantTitle || '',
        model: pendingChoice.model || '',
        color: pendingChoice.color || '',
        image: pendingChoice.image || '',
        kind: 'choice',
      });
    }
    // A variant can be present in more than one legacy source (for example
    // fixed products plus a pending choice). Keep one entry per variant so a
    // single gift can never be posted with quantity 2 by the reconciliation
    // layer.
    const uniqueDetails = [];
    const seenVariantIds = new Set();
    canonicalAutomaticGifts(details, context.chairModel || context.handle || '', color).forEach((detail) => {
      const variantId = Number(detail.id || 0);
      if (!variantId || seenVariantIds.has(variantId)) return;
      seenVariantIds.add(variantId);
      uniqueDetails.push(detail);
    });
    return uniqueDetails;
  }

  function resolvedGiftBundle(context) {
    return [...new Set(resolvedGiftDetails(context).map((gift) => Number(gift.id)).filter(Boolean))];
  }

  let reconcileTimer = null;
  let reconciling = false;
  let rerunAfterReconcile = false;
  let pendingChoiceState = false;
  let promoToastPending = false;
  let promoToastTimer = null;
  let promoToastRetryTimer = null;
  let promoToastRetryCount = 0;
  let promoToastNotBefore = 0;
  let promoCartMutationUntil = 0;
  let promoDrawerRefreshUntil = 0;
  // Prevent two confirmation clicks (or a delegated click plus the direct
  // card handler) from posting the same customer-choice gift twice.
  let choiceSelectionInFlight = false;
  // Used to protect a just-made preselection from an older cart request. A
  // slow /cart.js response from page startup or a queued reconciliation must
  // never clear a choice the customer made after that request began.
  let pendingChoiceWrittenAt = 0;

  // Keep an explicit customer gift removal separate from the promotion bundle.
  // The bundle remains on the chair so its discount and promotion metadata stay
  // intact, while reconciliation can respect a gift the customer declined.
  const dismissedGiftsStorageKey = () => `promo-dismissed-gifts:${config.campaign}`;
  let dismissedGiftsMemory = [];

  const readDismissedGifts = () => {
    const key = dismissedGiftsStorageKey();
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(stored)) {
        dismissedGiftsMemory = stored;
        return stored;
      }
    } catch (_) {}
    return dismissedGiftsMemory;
  };

  const writeDismissedGifts = (gifts) => {
    dismissedGiftsMemory = gifts;
    try { localStorage.setItem(dismissedGiftsStorageKey(), JSON.stringify(gifts)); } catch (_) {}
  };

  const dismissedGiftMatches = (gift, parentKey, variantId) =>
    String(gift?.parentKey || '') === String(parentKey || '')
    && Number(gift?.variantId || 0) === Number(variantId || 0);

  const isGiftDismissed = (parentKey, variantId) =>
    Boolean(parentKey && variantId)
    && readDismissedGifts().some((gift) => dismissedGiftMatches(gift, parentKey, variantId));

  const dismissGift = (parentKey, variantId, type = 'auto', quantity = 1) => {
    if (!parentKey || !variantId) return;
    const gifts = readDismissedGifts().filter((gift) => !dismissedGiftMatches(gift, parentKey, variantId));
    gifts.push({
      parentKey: String(parentKey),
      variantId: Number(variantId),
      type: String(type || 'auto'),
      quantity: Math.max(1, Number(quantity) || 1),
    });
    writeDismissedGifts(gifts);
  };

  const clearDismissedGift = (parentKey, variantId) => {
    if (!parentKey || !variantId) return;
    const gifts = readDismissedGifts().filter((gift) => !dismissedGiftMatches(gift, parentKey, variantId));
    writeDismissedGifts(gifts);
  };

  const pruneDismissedGifts = (cart) => {
    const parentKeys = new Set(getParentLines(cart).map((item) => String(item.key || '')));
    const gifts = readDismissedGifts();
    const active = gifts.filter((gift) => parentKeys.has(String(gift.parentKey || '')));
    if (active.length !== gifts.length) writeDismissedGifts(active);
  };

  // The product form and the promotion engine can both observe one cart add.
  // While the promotion engine is reconciling, the cart icon must wait for
  // the final server count instead of incrementing once per intermediate
  // event.
  window.__promoCartSyncing = false;

  const pendingChoiceStorageKey = () => productContext?.productId
    ? `promo-pending-choice:${productContext.productId}`
    : '';
  const readPendingChoice = () => {
    const key = pendingChoiceStorageKey();
    const runtimeChoice = window.__promoPendingChoice;
    if (runtimeChoice?.variantId
      && (!runtimeChoice.productId || Number(runtimeChoice.productId) === Number(productContext?.productId))) {
      return runtimeChoice;
    }
    if (!key) return null;
    try {
      const value = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (value?.variantId
        && (!value.productId || Number(value.productId) === Number(productContext?.productId))) {
        window.__promoPendingChoice = value;
        return value;
      }
      return null;
    } catch (_) {
      return null;
    }
  };
  const writePendingChoice = (choice, options = {}) => {
    const key = pendingChoiceStorageKey();
    const value = {
      ...choice,
      productId: Number(productContext?.productId || 0),
      // Preserve only a deliberate preselection made before the chair exists.
      // A choice being attached to an existing chair must be cleared if that
      // chair is later removed from the cart.
      _promo_pending_status: options.status || 'preselected',
      _promo_pending_parent_key: String(options.parentKey || ''),
    };
    pendingChoiceWrittenAt = Date.now();
    window.__promoPendingChoice = value;
    if (!key) return;
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const clearPendingChoice = () => {
    const key = pendingChoiceStorageKey();
    if (window.__promoPendingChoice
      && (!productContext?.productId || Number(window.__promoPendingChoice.productId || productContext.productId) === Number(productContext.productId))) {
      delete window.__promoPendingChoice;
    }
    pendingChoiceWrittenAt = 0;
    if (!key) return;
    try { sessionStorage.removeItem(key); } catch (_) {}
  };

  // A choice made before the chair is added is intentionally kept in
  // sessionStorage so it can be attached to the next chair add. In contrast,
  // an attaching choice belongs to a cart chair and must be reset as soon as
  // that chair disappears.
  const clearStalePendingChoice = (cart, requestStartedAt = 0) => {
    if (pendingChoiceWrittenAt && pendingChoiceWrittenAt >= Number(requestStartedAt || 0)) return;
    const pending = readPendingChoice();
    if (!pending?.variantId) return;
    if (pending._promo_pending_status === 'preselected') return;
    const hasMatchingParent = getParentLines(cart).some((item) =>
      Number(item.product_id) === Number(pending.productId || productContext?.productId)
    );
    if (hasMatchingParent) return;
    clearPendingChoice();
    document.dispatchEvent(new CustomEvent('promo:pending-choice-updated'));
  };

  const campaignEnd = promoTime.parseCampaignEnd(configNode.dataset.campaignEnd || '');
  const isGiftCampaign = () => ['tishrei', 'november'].includes(config.campaign)
    && (!Number.isFinite(campaignEnd) || promoTime.now() < campaignEnd);

  function couponStackingProperties() {
    return {
      _promo_coupon_stacking_enabled: config.couponStackingEnabled ? 'true' : 'false',
    };
  }

  function privateProperties(context) {
    if (!context || !context.eligible || context.excluded) return {};
    const bundle = resolvedGiftBundle(context);
    const bundleMeta = resolvedGiftDetails(context);
    return {
      _promo_eligible: 'true',
      _promo_bundle: JSON.stringify(bundle),
      _promo_bundle_meta: JSON.stringify(bundleMeta),
      _promo_choice_enabled: context.choiceEnabled ? 'true' : 'false',
      _promo_warranty_months: String(context.warrantyMonths || 0),
      _promo_chair_model: String(context.chairModel || context.handle || ''),
      _promo_chair_color: String(context.chairColor || ''),
      ...couponStackingProperties(),
    };
  }

  function parseRequestBody(body) {
    if (!body) return null;
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (error) { return null; }
    }
    if (body instanceof URLSearchParams) {
      const id = body.get('id') || body.get('items[0][id]');
      if (!id) return null;
      return { id: Number(id), quantity: Number(body.get('quantity') || 1), properties: {} };
    }
    return null;
  }

  function addContextToFormData(formData) {
    if (!(formData instanceof FormData) || !productContext?.eligible || productContext?.excluded) return formData;
    const selectedVariantId = formData.get('id');
    // The colour switcher publishes the selected variant before the replaced
    // form is fully settled. Prefer that marker when present, otherwise use
    // the form's own id. This prevents a stale Graphite id from rebuilding a
    // Glacier chair's exact gift bundle.
    const authoritativeVariantId = Number(urlVariantId() || window.__promoSelectedVariantId || selectedVariantId || productContext.variantId || 0);
    if (authoritativeVariantId) {
      applyPromoVariantContext(authoritativeVariantId);
      if (String(selectedVariantId || '') !== String(authoritativeVariantId)) {
        formData.set('id', String(authoritativeVariantId));
      }
    }
    const properties = privateProperties(productContext);
    Object.entries(properties).forEach(([key, value]) => {
      if (!formData.has(`properties[${key}]`)) formData.append(`properties[${key}]`, value);
    });
    return formData;
  }

  function addContextToBody(parsed) {
    if (!parsed || !productContext || !productContext.eligible || productContext.excluded) return parsed;
    if (Array.isArray(parsed.items)) {
      const parentIndex = parsed.items.findIndex((item) => item?.properties?._main_chair_product === 'true');
      const targetIndex = parentIndex >= 0 ? parentIndex : 0;

      // A Standard/Pro bundle is posted as one JSON request containing the
      // chair plus its paid footrest/battery lines. If the selected chair
      // variant has an exact auto-gift reference for either accessory, remove
      // that paid bundle line here. This is a final guard for the brief race
      // where the old product form is submitted while the colour-switched
      // section is still being replaced; the exact free variant is then added
      // by reconciliation from the selected variant metafield.
      const authoritativeVariantId = Number(urlVariantId() || window.__promoSelectedVariantId || parsed.items[targetIndex]?.id || productContext.variantId || 0);
      if (authoritativeVariantId) applyPromoVariantContext(authoritativeVariantId);
      // Build the parent promo bundle only after the selected variant has
      // been applied. Otherwise a colour-switched chair can carry the old
      // colour's _promo_bundle into the cart even though its UI is current.
      const properties = privateProperties(productContext);
      const exactGifts = Array.isArray(productContext.fixedVariants) ? productContext.fixedVariants : [];
      // A customer-selected choice is also a free gift. Include it here so a
      // Standard/Pro bundle cannot leave its paid battery/footrest beside the
      // selected free variant while the chair is being added.
      const resolvedGifts = resolvedGiftDetails(productContext);
      const hasGiftKind = (kind) => [...exactGifts, ...resolvedGifts].some((gift) => {
        const giftVariantId = Number(gift?.variantId || gift?.id || 0);
        if (!gift || gift.available === false || !giftVariantId) return false;
        const text = normalize([gift.handle, gift.title, gift.productTitle, gift.variantTitle].filter(Boolean).join(' '));
        return kind === 'battery'
          ? /battery|batter/.test(text)
          : /footrest|foot support|foot-support/.test(text);
      });
      const shouldDropPaidAccessory = (item) => {
        if (!item || item.properties?._chair_accessory !== 'true') return false;
        const accessoryName = normalize(item.properties?._accessory_name || item.product_title || item.title || '');
        if (/battery|batter/.test(accessoryName)) return hasGiftKind('battery');
        if (/footrest|foot support|foot-support/.test(accessoryName)) return hasGiftKind('footrest');
        return false;
      };

      parsed.items = parsed.items.filter((item, index) => index === targetIndex || !shouldDropPaidAccessory(item));
      const nextParentIndex = parsed.items.findIndex((item) => item?.properties?._main_chair_product === 'true');
      parsed.items = parsed.items.map((item, index) => {
        if (index !== (nextParentIndex >= 0 ? nextParentIndex : 0)) {
          if (authoritativeVariantId && item?.properties?._chair_accessory === 'true') {
            return {
              ...item,
              properties: { ...(item.properties || {}), _linked_to: String(authoritativeVariantId) },
            };
          }
          return item;
        }
        return {
          ...item,
          ...(authoritativeVariantId ? { id: authoritativeVariantId } : {}),
          properties: { ...properties, ...(item.properties || {}) },
        };
      });
    } else if (parsed.id) {
      const authoritativeVariantId = Number(urlVariantId() || window.__promoSelectedVariantId || parsed.id || productContext.variantId || 0);
      if (authoritativeVariantId) {
        parsed.id = authoritativeVariantId;
        applyPromoVariantContext(authoritativeVariantId);
      }
      const properties = privateProperties(productContext);
      parsed.properties = { ...properties, ...(parsed.properties || {}) };
    }
    return parsed;
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const requestHeaders = new Headers(init.headers || {});
    const isCartRecommendation = requestHeaders.get('X-Newton-Cart-Recommendation') === 'true';
    const isAccessoryAdd = requestHeaders.get('X-Newton-Accessory-Add') === 'true';
    const bypassProductPromoContext = isCartRecommendation || isAccessoryAdd;
    if (url.includes('/cart/add.js') && !bypassProductPromoContext && productContext?.eligible && !productContext?.excluded) {
      window.__promoCartSyncing = isGiftCampaign();
      promoToastPending = true;
      promoToastRetryCount = 0;
      promoToastNotBefore = Date.now() + 350;
      window.clearTimeout(promoToastRetryTimer);
      if (productContext.choiceEnabled) window.__promoChoicePending = true;
      if (init.body instanceof FormData) {
        init = { ...init, body: addContextToFormData(init.body) };
      }
      const parsed = parseRequestBody(init.body);
      if (parsed) {
        init = { ...init, body: JSON.stringify(addContextToBody(parsed)) };
        init.headers = { ...(init.headers || {}), 'Content-Type': 'application/json' };
      }
    }

    const response = await nativeFetch(input, init);
    if (url.includes('/cart/change.js') || url.includes('/cart/update.js')) {
      // A drawer remove/update can briefly leave the old parent line in the
      // cart response. Do not let that transient state open the choice modal.
      promoCartMutationUntil = Math.max(promoCartMutationUntil, Date.now() + 1200);
    }
    if (!bypassProductPromoContext && (url.includes('/cart/add.js') || url.includes('/cart/change.js') || url.includes('/cart/update.js'))) {
      pendingChoiceState = false;
      scheduleReconcile();
    }
    if (!bypassProductPromoContext && response.ok && url.includes('/cart/add.js') && productContext?.choiceEnabled) {
      window.setTimeout(() => {
        void maybeOpenChoiceAfterAdd();
      }, 260);
    }
    return response;
  };

  async function getCart() {
    const response = await nativeFetch(config.cartUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  function getProperties(item) {
    return item.properties || {};
  }

  const propertyIsTrue = (item, key) => String(getProperties(item)[key] ?? '').toLowerCase() === 'true';
  const isPromoGift = (item) => propertyIsTrue(item, '_promo_gift');

  function getParentLines(cart) {
    return cart.items.filter((item) => {
      const props = getProperties(item);
      return propertyIsTrue(item, '_promo_eligible') && !isPromoGift(item);
    });
  }

  function selectedChoiceQuantity(cart, parent) {
    const selectedQuantity = cart.items
      .filter((item) => getProperties(item)._promo_parent_key === parent.key
        && getProperties(item)._promo_gift_type === 'choice')
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const dismissedQuantity = readDismissedGifts()
      .filter((gift) => String(gift.parentKey || '') === String(parent.key || '')
        && gift.type === 'choice')
      .reduce((sum, gift) => sum + Math.max(1, Number(gift.quantity) || 1), 0);
    return selectedQuantity + dismissedQuantity;
  }

  // A gift selected from the PDP tile is intentionally stored until the
  // chair is added. Count that stored selection while checking the choice
  // requirement so the reminder/picker never reopens for an already chosen
  // gift during the short cart reconciliation window.
  function pendingChoiceQuantityForParent(parent) {
    const pending = readPendingChoice();
    if (!pending?.variantId) return 0;
    const contextProductId = Number(productContext?.productId || 0);
    const parentProductId = Number(parent?.product_id || 0);
    const contextVariantId = Number(productContext?.variantId || 0);
    const parentVariantId = Number(parent?.variant_id || 0);
    if (!contextProductId || parentProductId !== contextProductId) return 0;
    if (contextVariantId && parentVariantId && parentVariantId !== contextVariantId) return 0;
    return 1;
  }

  function updateChoiceReminder(cart) {
    const reminder = document.querySelector('[data-promo-choice-reminder]');
    const link = reminder?.querySelector('[data-promo-choice-reminder-link]');
    if (!reminder || !link) return;
    if (!isGiftCampaign()) {
      reminder.hidden = true;
      return;
    }

    const parents = getParentLines(cart).filter((item) => getProperties(item)._promo_choice_enabled === 'true'
      && choiceModelAllowed(getProperties(item)._promo_chair_model || item.product_title || item.title));
    const missing = parents.find((parent) => {
      const chosen = selectedChoiceQuantity(cart, parent) + pendingChoiceQuantityForParent(parent);
      return chosen < Math.min(Number(parent.quantity || 0), config.maxUnits);
    });
    if (!missing) {
      reminder.hidden = true;
      return;
    }
    reminder.hidden = false;
    link.href = `${missing.url || '/cart'}${missing.url?.includes('?') ? '&' : '?'}promo_choose=1`;
  }

  function hasPendingChoice(cart) {
    if (!isGiftCampaign()) return false;
    return getParentLines(cart)
      .filter((item) => getProperties(item)._promo_choice_enabled === 'true'
        && choiceModelAllowed(getProperties(item)._promo_chair_model || item.product_title || item.title))
      .some((parent) => {
        const chosen = selectedChoiceQuantity(cart, parent) + pendingChoiceQuantityForParent(parent);
        return chosen < Math.min(Number(parent.quantity || 0), config.maxUnits);
      });
  }

  // The add-to-cart hook can finish before reconciliation has attached a
  // preselected choice. Check the actual cart before opening the modal. This
  // prevents the second trigger when the customer chose a gift from the tile
  // first, while still opening it for the normal "add chair, then choose"
  // flow.
  let choiceOpenCheckInFlight = false;
  async function maybeOpenChoiceAfterAdd() {
    if (!productContext?.choiceEnabled || choiceOpenCheckInFlight) return;
    if (Date.now() < promoCartMutationUntil) return;
    const hasStoredPendingChoice = Boolean(readPendingChoice());
    choiceOpenCheckInFlight = true;
    try {
      const cart = await getCart();
      const pending = hasPendingChoice(cart);
      if (Date.now() < promoCartMutationUntil || !pending) {
        // This also releases product-form's drawer gate when a choice was
        // already selected from the tile and attached with the chair.
        window.__promoChoicePending = false;
        return;
      }
      if (hasStoredPendingChoice) return;
      document.dispatchEvent(new CustomEvent('promo:choice-open'));
    } catch (_) {
      // Reconciliation will emit promo:choice-pending if the choice is still
      // missing; do not open a modal from a stale/intermediate response.
    } finally {
      choiceOpenCheckInFlight = false;
    }
  }

  function emitPendingChoice(cart) {
    const pending = hasPendingChoice(cart);
    const hasPreselectedChoice = Boolean(readPendingChoice()?.variantId);
    // Product-form/cart-drawer integrations use this gate to decide whether
    // the drawer can open after an add. Keep it tied to the server-confirmed
    // cart state: a preselected choice is already attached (false), while an
    // ordinary add still needs the picker (true).
    // While a preselected gift is being attached, keep the drawer gated but
    // do not open the picker. The next reconciliation clears the pending
    // flag after the selected line is confirmed in the cart.
    window.__promoChoicePending = pending;
    if (pending && !hasPreselectedChoice && !pendingChoiceState) {
      document.dispatchEvent(new CustomEvent('promo:choice-pending'));
    }
    pendingChoiceState = pending && !hasPreselectedChoice;
  }

  function clearPendingChoiceIfAttached(cart, requestStartedAt = 0) {
    const pending = readPendingChoice();
    if (!pending?.variantId || !productContext?.productId) return;
    if (pendingChoiceWrittenAt && pendingChoiceWrittenAt >= Number(requestStartedAt || 0)) return;
    // An empty cart is a valid state while the customer is choosing a gift
    // before adding the chair. Do not erase that preselection from a queued
    // cart reconciliation: the next chair add needs the exact variant. Stale
    // values from a previous session are cleared by the initial startup check
    // above (after the cart has been read), not by every later cart event.
    if (Number(cart?.item_count || 0) === 0) {
      return;
    }
    const parents = getParentLines(cart).filter((item) => Number(item.product_id) === Number(productContext.productId));
    const parentKeys = new Set(parents.map((item) => item.key));
    const attached = cart.items.some((item) => Number(item.variant_id) === Number(pending.variantId)
      && parentKeys.has(getProperties(item)._promo_parent_key)
      && getProperties(item)._promo_gift_type === 'choice');
    if (attached) clearPendingChoice();
  }

  function hasPaidFootrest(cart, parent) {
    const parentProps = getProperties(parent);
    const parentBundleUuid = String(parentProps._bundle_uuid || '');
    const parentVariantId = String(parent.variant_id || '');
    return cart.items.some((item) => {
      if (isPromoGift(item)) return false;
      const props = getProperties(item);
      const itemText = normalize(item.product_title || item.title || item.handle || props._newton_handle || '');
      const isFootrest = String(props._newton_handle || '').includes('foot-support')
        || /footrest|foot support/.test(itemText);
      if (!isFootrest) return false;
      return (parentBundleUuid && String(props._bundle_uuid || '') === parentBundleUuid)
        || (parentVariantId && String(props._linked_to || '') === parentVariantId);
    });
  }

  function expectedGiftLines(cart) {
    if (!isGiftCampaign()) return [];
    const lines = [];
    for (const parent of getParentLines(cart)) {
      const props = getProperties(parent);
      let bundle = [];
      let bundleMeta = [];
      try { bundle = JSON.parse(props._promo_bundle || '[]'); } catch (error) { bundle = []; }
      try { bundleMeta = JSON.parse(props._promo_bundle_meta || '[]'); } catch (error) { bundleMeta = []; }
      // If this is the product/variant currently open, rebuild the bundle
      // from the fresh product context. This repairs carts created before a
      // colour switch (for example a Glacier footrest left on Graphite).
      const isCurrentProductVariant = productContext
        && Number(productContext.productId) === Number(parent.product_id)
        && Number(productContext.variantId) === Number(parent.variant_id);
      const parentModel = String(props._promo_chair_model || (isCurrentProductVariant ? productContext.chairModel : '') || '').trim();
      const parentColor = String(props._promo_chair_color || (isCurrentProductVariant ? productContext.chairColor : '') || '').trim();
      if (isCurrentProductVariant) {
        bundle = resolvedGiftBundle(productContext);
        bundleMeta = resolvedGiftDetails(productContext);
      }
      // Keep the free footrest when the selected promotion explicitly
      // configures one. A paid Standard/Pro accessory can be left in the cart
      // for one request while a colour-switched section is being replaced;
      // reconciliation removes that paid line below. Only retain the legacy
      // guard when there is no promotional footrest reference at all.
      const hasPromotionalFootrest = bundleMeta.some((gift) => /footrest|foot support/i.test(
        String(gift.productTitle || gift.title || gift.variantTitle || '')
      ));
      if (hasPaidFootrest(cart, parent) && !hasPromotionalFootrest && !/maxis/.test(normalize(parentModel))) {
        const paidFootrestIds = new Set(bundleMeta
          .filter((gift) => /footrest|foot support/i.test(String(gift.productTitle || gift.title || gift.variantTitle || '')))
          .map((gift) => Number(gift.id)));
        bundle = bundle.filter((variantId) => !paidFootrestIds.has(Number(variantId)));
        bundleMeta = bundleMeta.filter((gift) => !paidFootrestIds.has(Number(gift.id)));
      }
      if (!Array.isArray(bundle)) continue;
      const quantity = Math.min(parent.quantity, config.maxUnits);
      bundle.forEach((variantId) => {
        if (!variantId) return;
        if (isGiftDismissed(parent.key, variantId)) return;
        const meta = bundleMeta.find((gift) => Number(gift.id) === Number(variantId)) || {};
        const displayModel = String(meta.model || '').trim();
        const displayColor = String(meta.color || '').trim();
        const displayVariant = displayModel && displayColor && displayModel.toLowerCase() !== displayColor.toLowerCase()
          ? `${displayModel} / ${displayColor}`
          : (displayModel || displayColor);
        lines.push({
          variantId: Number(variantId),
          quantity,
          parentKey: parent.key,
          parentVariantId: parent.variant_id,
          properties: {
            _promo_gift: 'true',
            _promo_campaign: config.campaign,
            _promo_parent: String(parent.variant_id),
            _promo_parent_key: parent.key,
            _promo_gift_type: meta.kind === 'warranty' ? 'warranty' : (meta.kind === 'choice' ? 'choice' : 'auto'),
            _promo_non_physical: meta.kind === 'warranty' ? 'true' : 'false',
            _promo_warranty_months: meta.kind === 'warranty' ? String(props._promo_warranty_months || 0) : '',
            _promo_gift_product_title: meta.productTitle || meta.title || '',
            _promo_gift_variant_title: meta.variantTitle || meta.title || '',
            _promo_gift_model: meta.model || '',
            _promo_gift_color: meta.color || '',
            _promo_gift_display_variant: displayVariant,
            _promo_parent_model: parentModel,
            _promo_parent_color: parentColor,
            ...couponStackingProperties(),
          },
        });
      });
    }
    return lines;
  }

  async function addGift(line) {
    const response = await nativeFetch(config.addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: line.variantId, quantity: line.quantity, properties: line.properties }],
      }),
    });
    if (!response.ok) throw new Error(`Gift add failed: ${response.status}`);
  }

  async function changeLine(key, quantity, properties) {
    const response = await nativeFetch(config.changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity, ...(properties ? { properties } : {}) }),
    });
    if (!response.ok) throw new Error(`Gift change failed: ${response.status}`);
  }

  // Remove/update several gift lines in one cart request. The previous
  // implementation awaited one /cart/change.js request at a time, so a chair
  // removal with two or more gifts could take several UI refreshes to settle.
  async function updateCartQuantities(updates) {
    const entries = Object.entries(updates || {}).filter(([key, quantity]) => key && Number.isFinite(Number(quantity)));
    if (!entries.length) return;
    const response = await nativeFetch(config.updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: Object.fromEntries(entries.map(([key, quantity]) => [key, Number(quantity)])) }),
    });
    if (!response.ok) throw new Error(`Gift batch update failed: ${response.status}`);
  }

  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    const cartItems = cartDrawer?.querySelector('cart-items-component') || document.querySelector('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId || cartDrawer?.dataset.sectionId;
    if (!sectionId) return;

    const response = await nativeFetch(`/?section_id=${encodeURIComponent(sectionId)}`, {
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return;

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const nextCartItems = documentFragment.querySelector('cart-items-component');
    if (!nextCartItems || !cartItems.parentNode) return;

    cartItems.parentNode.replaceChild(nextCartItems, cartItems);
    promoDrawerRefreshUntil = Date.now() + 700;
    window.setTimeout(() => {
      if (typeof window.groupCartItems === 'function') window.groupCartItems();
    }, 25);
  }

  function refreshCartBubble(cart) {
    // Match the server-rendered cart bubble: automatic/hidden dependencies
    // must not inflate the customer-visible cart count.
    const count = Array.isArray(cart?.items)
      ? cart.items.reduce((total, item) => {
        const props = item.properties || {};
        const hidden = props._free_product === 'true'
          || props._gas_lift_dependency === 'true'
          || props._hidden_from_customer === 'true'
          || props._automatic_accessory === 'true'
          || (props._chair_accessory === 'true' && props._seat_accessory !== 'true');
        return hidden ? total : total + Number(item.quantity || 0);
      }, 0)
      : Number(cart?.item_count || 0);
    const countText = count > 0 && count < 100 ? String(count) : '';

    // Updating cart-count text creates childList mutations. Those are UI
    // output from this reconciliation, not a new cart change, so keep the
    // drawer observer quiet while its MutationObserver callback is delivered.
    promoDrawerRefreshUntil = Math.max(promoDrawerRefreshUntil, Date.now() + 100);
    document.querySelectorAll('.cart-bubble__text-count, [ref="cartBubbleCount"], .cart-count, [data-cart-count]').forEach((node) => {
      // cart-icon owns its own count. Writing it here and again through
      // renderCartBubble() made the text oscillate between "" and "0" for an
      // empty cart, producing a mutation on every reconciliation pass.
      if (node.closest('cart-icon')) return;
      if (node.textContent !== countText) node.textContent = countText;
      node.classList.toggle('hidden', count === 0);
    });
    document.querySelectorAll('.cart-bubble, .cart-count-bubble').forEach((node) => {
      node.classList.toggle('visually-hidden', count === 0);
      node.classList.toggle('hidden', count === 0);
    });
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble !== 'function') return;
      if (Number(icon.currentCartCount) === count) return;
      icon.renderCartBubble(count, false, false);
    });
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (_) {}
  }

  async function reconcile() {
    // A choice replacement owns the cart while it removes the previous gift,
    // adds the new one, and updates the parent bundle. Running reconciliation
    // against the old bundle during that window can re-add the previous gift
    // and create a duplicate line. Queue one pass for after the replacement
    // has finished instead.
    if (choiceSelectionInFlight) {
      if (reconciling) rerunAfterReconcile = true;
      return;
    }
    if (reconciling) {
      rerunAfterReconcile = true;
      return;
    }
    const requestStartedAt = Date.now();
    reconciling = true;
    try {
      const cart = await getCart();
      // The customer may have started a replacement while this cart read was
      // in flight. Do not apply the now-stale bundle; the replacement will
      // schedule a fresh reconciliation after its parent line is updated.
      if (choiceSelectionInFlight) {
        rerunAfterReconcile = true;
        return;
      }
      pruneDismissedGifts(cart);
      clearStalePendingChoice(cart, requestStartedAt);
      updateChoiceReminder(cart);
      const expected = expectedGiftLines(cart);
      const actual = cart.items.filter(isPromoGift);
      const parentsByKey = new Map(getParentLines(cart).map((parent) => [parent.key, parent]));
      const expectedByKey = new Map();
      const removals = {};
      const quantityUpdates = {};
      const propertyChanges = [];
      let cartChanged = false;
      expected.forEach((line) => expectedByKey.set(`${line.variantId}|${line.parentKey}`, line));
      const expectedChoiceParents = new Set([...expectedByKey.values()]
        .filter((line) => line.properties?._promo_gift_type === 'choice')
        .map((line) => String(line.parentKey || '')));
      const unmatchedChoiceKeysSeen = new Set();

      // A bundle request can race the promo engine after a colour switch. In
      // that window the paid Footrest/Battery line may reach Shopify before
      // the exact free variant is reconciled, which makes the drawer show a
      // quantity of 2 (paid + free). Remove only bundle accessories tied to
      // this parent; customer-selected gifts are marked _promo_gift and are
      // therefore left alone.
      const giftKindsByParent = new Map();
      expected.forEach((line) => {
        const parentKey = String(line.parentKey || '');
        const text = normalize([
          line.properties?._promo_gift_variant_title,
          line.properties?._promo_gift_product_title,
          line.properties?._promo_gift_display_variant,
        ].filter(Boolean).join(' '));
        const kind = /battery|batter/.test(text)
          ? 'battery'
          : (/footrest|foot support|foot-support/.test(text) ? 'footrest' : '');
        if (!parentKey || !kind) return;
        if (!giftKindsByParent.has(parentKey)) giftKindsByParent.set(parentKey, new Set());
        giftKindsByParent.get(parentKey).add(kind);
      });
      const parentLines = getParentLines(cart);
      const accessoryKind = (item) => {
        const props = getProperties(item);
        const text = normalize([
          props._accessory_name,
          props._newton_handle,
          item.product_title,
          item.title,
          item.handle,
        ].filter(Boolean).join(' '));
        if (/battery|batter/.test(text)) return 'battery';
        if (/footrest|foot support|foot-support/.test(text)) return 'footrest';
        return '';
      };
      const isAccessoryForParent = (item, parent) => {
        const itemProps = getProperties(item);
        const parentProps = getProperties(parent);
        const itemBundleUuid = String(itemProps._bundle_uuid || '');
        const parentBundleUuid = String(parentProps._bundle_uuid || '');
        if (itemBundleUuid && parentBundleUuid) return itemBundleUuid === parentBundleUuid;
        return String(itemProps._linked_to || '') === String(parent.variant_id || '');
      };
      cart.items.forEach((item) => {
        if (isPromoGift(item)) return;
        const props = getProperties(item);
        if (props._chair_accessory !== 'true') return;
        const kind = accessoryKind(item);
        if (!kind) return;
        const parent = parentLines.find((candidate) => isAccessoryForParent(item, candidate));
        if (!parent) return;
        if (giftKindsByParent.get(parent.key)?.has(kind)) removals[item.key] = 0;
      });

      if (!isGiftCampaign()) {
        const campaignRemovals = { ...removals, ...Object.fromEntries(actual.map((item) => [item.key, 0])) };
        await updateCartQuantities(campaignRemovals);
        cartChanged = Object.keys(campaignRemovals).length > 0;
        const latestCart = cartChanged ? await getCart() : cart;
        if (cartChanged) await refreshCartDrawer();
        refreshCartBubble(latestCart);
        syncPromoGiftToastButton({ ...latestCart, items: latestCart.items.filter((item) => !isPromoGift(item)) });
        pendingChoiceState = false;
        document.dispatchEvent(new CustomEvent('promo:cart-reconciled', {
          detail: { cart: latestCart },
        }));
        return;
      }

      for (const item of actual) {
        const props = getProperties(item);
        if (props._promo_gift_type === 'choice') {
          if (!isGiftCampaign() || !hasParentForChoice(item, cart)) {
            removals[item.key] = 0;
            continue;
          }
          // A preselected choice is serialized into the chair's promo bundle
          // and later reconciled like every other gift. If the choice line is
          // already present, consume the expected entry so reconciliation
          // does not add a second copy (Shopify then displays quantity 2).
          const choiceKey = `${item.variant_id}|${props._promo_parent_key || ''}`;
          const wantedChoice = expectedByKey.get(choiceKey);
          if (wantedChoice) {
            expectedByKey.delete(choiceKey);
            const stackingPropertiesChanged = ['_promo_coupon_stacking_enabled']
              .some((name) => String(props[name] || '') !== String(wantedChoice.properties?.[name] || ''));
            if (stackingPropertiesChanged) {
              propertyChanges.push([item.key, wantedChoice.quantity, wantedChoice.properties]);
            } else if (item.quantity !== wantedChoice.quantity) {
              quantityUpdates[item.key] = wantedChoice.quantity;
            }
          } else {
            // A replacement can race with a cart reconciliation and briefly
            // leave two choice lines for the same parent/variant. The first
            // line consumes the expected bundle entry above; any additional
            // line is a duplicate and must be removed instead of being left
            // in the cart (which would show the gift twice). Keep a legacy
            // unmatched choice when the parent has no expected choice at all;
            // older carts may not have serialized that selection in the
            // parent's _promo_bundle yet.
            const parentKey = String(props._promo_parent_key || '');
            if (expectedChoiceParents.has(parentKey) || unmatchedChoiceKeysSeen.has(choiceKey)) {
              removals[item.key] = 0;
            } else {
              unmatchedChoiceKeysSeen.add(choiceKey);
            }
          }
          continue;
        }
        const parent = parentsByKey.get(props._promo_parent_key);
        const parentText = normalize(parent?.properties?._promo_chair_model || parent?.product_title || parent?.title || '');
        const giftText = normalize(item.product_title || item.title || item.handle || '');
        const key = `${item.variant_id}|${props._promo_parent_key || ''}`;
        const wanted = expectedByKey.get(key);
        // Remove legacy automatic gifts that do not belong to Maxis. A
        // customer-selected choice gift is intentionally left untouched.
        if (parentText.includes('maxis')
          && props._promo_gift_type !== 'warranty'
          && wanted?.properties?._promo_gift_type !== 'warranty'
          && !/mouse|pad|footrest|foot support|warranty|year/.test(giftText)) {
          removals[item.key] = 0;
          continue;
        }
        if (!wanted) {
          removals[item.key] = 0;
          continue;
        }
        expectedByKey.delete(key);
        const propertiesChanged = ['_promo_gift_model', '_promo_gift_color', '_promo_gift_variant_title', '_promo_gift_display_variant', '_promo_parent_model', '_promo_parent_color', '_promo_gift_type', '_promo_non_physical', '_promo_warranty_months', '_promo_coupon_stacking_enabled']
          .some((name) => String(props[name] || '') !== String(wanted.properties?.[name] || ''));
        if (item.quantity !== wanted.quantity || props._promo_campaign !== config.campaign || propertiesChanged) {
          if (propertiesChanged || props._promo_campaign !== config.campaign) {
            propertyChanges.push([item.key, wanted.quantity, wanted.properties]);
          } else {
            quantityUpdates[item.key] = wanted.quantity;
          }
        }
      }

      // Commit all removals/quantity corrections before requesting the cart
      // drawer section once. This prevents the drawer from flickering through
      // an intermediate state for every gift line.
      if (Object.keys(removals).length) {
        await updateCartQuantities(removals);
        cartChanged = true;
      }
      if (Object.keys(quantityUpdates).length) {
        await updateCartQuantities(quantityUpdates);
        cartChanged = true;
      }
      if (propertyChanges.length) {
        await Promise.all(propertyChanges.map(([key, quantity, properties]) => changeLine(key, quantity, properties)));
        cartChanged = true;
      }

      // A replacement can begin while one of the corrective cart requests
      // above is awaiting Shopify. Never add gifts calculated from that stale
      // snapshot after the replacement has taken ownership of the cart.
      if (choiceSelectionInFlight) {
        rerunAfterReconcile = true;
        return;
      }

      // Add all automatic gifts together. Sequential requests made a two-gift
      // bundle appear to arrive many seconds after the chair.
      const missingGifts = [...expectedByKey.values()];
      if (missingGifts.length) {
        await Promise.all(missingGifts.map((line) => addGift(line)));
        cartChanged = true;
      }
      let latestCart = cart;
      if (cartChanged) {
        latestCart = await getCart();
        await refreshCartDrawer();
        refreshCartBubble(latestCart);
      } else {
        refreshCartBubble(cart);
      }
      clearPendingChoiceIfAttached(latestCart, requestStartedAt);
      // Clear a preselected choice before calculating the pending state. This
      // prevents the product form from staying gated after the selected gift
      // has been successfully attached to the chair.
      updateChoiceReminder(latestCart);
      emitPendingChoice(latestCart);
      syncPromoGiftToastButton(latestCart);
      if (promoToastPending) {
        // The product add should reveal only the sticky trigger. The customer
        // opens the full toast deliberately by clicking that trigger.
        promoToastPending = false;
        promoToastRetryCount = 0;
        window.clearTimeout(promoToastRetryTimer);
      }
      document.dispatchEvent(new CustomEvent('promo:cart-reconciled', {
        detail: { cart: latestCart },
      }));
    } catch (error) {
      console.warn('[Promo] Reconciliation skipped', error);
    } finally {
      reconciling = false;
      window.__promoCartSyncing = false;
      if (rerunAfterReconcile) {
        rerunAfterReconcile = false;
        scheduleReconcile();
      }
    }
  }

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 180);
  }

  // The theme's cart drawer integration also uses XMLHttpRequest. Observe
  // those mutations so removing a chair immediately removes its attached
  // promo gifts instead of leaving orphaned gift lines in the cart.
  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function promoOpen(method, url, ...args) {
    this.__promoCartRequest = String(url || '').includes('/cart/');
    this.__promoCartUrl = String(url || '');
    return nativeXhrOpen.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function promoSend(...args) {
    if (this.__promoCartRequest) {
      this.addEventListener('loadend', () => {
        if (this.__promoCartUrl.includes('/cart/change.js') || this.__promoCartUrl.includes('/cart/update.js')) {
          promoCartMutationUntil = Math.max(promoCartMutationUntil, Date.now() + 1200);
        }
        pendingChoiceState = false;
        scheduleReconcile();
        if (this.__promoCartUrl.includes('/cart/add.js')
          && this.status >= 200 && this.status < 300 && productContext?.choiceEnabled) {
          window.setTimeout(() => { void maybeOpenChoiceAfterAdd(); }, 320);
        }
      }, { once: true });
    }
    return nativeXhrSend.apply(this, args);
  };

  function getChoiceModal() {
    return document.querySelector('[data-promo-choice-modal]');
  }

  function setChoiceModalOpen(open) {
    const modal = getChoiceModal();
    if (!modal) return;
    if (!open) modal.dataset.promoChoiceReplace = 'false';
    modal.hidden = !open;
    modal.setAttribute('aria-hidden', String(!open));
    document.documentElement.classList.toggle('promo-choice-open', open);
  }

  // The choice modal is rendered once at layout level, while the colour
  // switcher replaces only the product section. Re-score every choice card
  // against the live product context so a later swatch change also updates
  // the variant that will actually be added (wheel colour mapping included).
  function refreshChoiceVariantCards() {
    const modal = getChoiceModal();
    if (!modal || !productContext) return;
    const model = modelKey([productContext.chairModel, productContext.handle].filter(Boolean).join(' '));
    const chairColor = String(productContext.chairColor || '').trim();
    const chairColorKey = colorKey(chairColor);

    modal.querySelectorAll('[data-promo-choice-item]').forEach((card) => {
      // Rest & Relax has one card per variant. These cards must keep their
      // own variant/image when the chair model or colour changes; otherwise
      // the general compatibility scorer would collapse all three back to
      // the same product variant.
      if (card.dataset.choiceStaticVariant === 'true') return;

      let variants = [];
      try { variants = JSON.parse(card.dataset.choiceVariants || '[]'); } catch (_) { variants = []; }
      if (!Array.isArray(variants) || !variants.length) return;

      const isBattery = card.dataset.choiceBattery === 'true';
      const handleText = normalize([card.dataset.choiceHandle, card.dataset.productTitle].filter(Boolean).join(' '));
      let targetColor = chairColorKey;
      if (handleText.includes('wheel')) {
        if (chairColorKey === 'glacier' || chairColorKey === 'grey') targetColor = 'grey';
        else if (chairColorKey === 'graphite' || chairColorKey === 'obsidian' || chairColorKey === 'black') targetColor = 'black';
        else if (chairColorKey === 'green') targetColor = 'green';
      }

      let best = null;
      let hasModelMatch = false;
      variants.forEach((variant) => {
        if (!variant || variant.available === false) return;
        const text = normalize([variant.title, variant.option1, variant.option2, variant.option3].filter(Boolean).join(' '));
        const modelMatch = Boolean(model && text.includes(model));
        const variantColor = colorKey([variant.option1, variant.option2, variant.option3, variant.title].filter(Boolean).join(' '));
        const oldOmniBattery = isBattery
          && text.includes('omni')
          && /midnight|space grey|space gray|moss green/.test(text)
          && !/omni gen|omni pro|maxis/.test(text);
        if (isBattery && model !== 'omni gen' && oldOmniBattery) return;
        if (modelMatch) hasModelMatch = true;
        let score = 5;
        if (modelMatch) score += 300;
        if (targetColor && variantColor === targetColor) score += 100;
        if (/default title/.test(text)) score -= 5;
        if (!best || score > best.score) best = { variant, score };
      });

      // Do not offer an old Omni battery as a Pro/Maxis choice. The scoring
      // above already removed those variants for non-Omni-Gen models. Keep a
      // remaining battery card visible when the catalogue uses a slightly
      // different compatibility label (for example a combined “Omni Pro /
      // Maxis Airflow” group); hiding it solely because the literal model
      // token was not found made the battery disappear from the popup.
      const hide = !best;
      card.hidden = hide || !best;
      card.disabled = hide || !best;
      if (!best) return;

      const variant = best.variant;
      card.dataset.variantId = String(variant.id || '');
      card.dataset.variantPrice = String(variant.price || 0);
      const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean).map(String);
      const modelOption = optionValues.find((value) => normalize(value).includes(model));
      const colorOption = optionValues.find((value) => colorKey(value) === variantColorKey(variant));
      card.dataset.productModel = modelOption || productContext.chairModel || '';
      card.dataset.productColor = colorOption || chairColor;
      const image = card.querySelector('.promo-choice-card__image');
      if (image && variant.image) {
        image.src = variant.image;
        image.removeAttribute('srcset');
      }
      const value = card.querySelector('.promo-choice-card__value');
      if (value && Number(variant.price || 0) > 0) value.textContent = `שווי ${moneyFromCents(variant.price)}`;
    });
  }

  function variantColorKey(variant) {
    return colorKey([variant?.option1, variant?.option2, variant?.option3, variant?.title].filter(Boolean).join(' '));
  }

  function moneyFromCents(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'he-IL', {
        style: 'currency', currency: 'ILS', maximumFractionDigits: 0,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `₪${Math.round(Number(cents || 0) / 100)}`;
    }
  }

  // Mobile browsers frequently render an iframe PDF with their own nested
  // viewer. That viewer does not consistently pass touch swipes through a
  // fixed Shopify modal, so render the PDF pages into the modal's own scroll
  // container on small screens. Desktop keeps the native viewer and the
  // external link remains available as a final fallback.
  let promoPdfJsPromise = null;
  let promoPdfWorkerSrc = '';
  let promoPdfResizeTimer = 0;
  const promoPdfSources = [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
      worker: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js',
      worker: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
    },
  ];

  function isMobilePromoTerms() {
    return Boolean(window.matchMedia?.('(max-width: 749px)').matches);
  }

  function loadPromoPdfJs() {
    if (window.pdfjsLib?.getDocument) {
      promoPdfWorkerSrc ||= promoPdfSources[0].worker;
      return Promise.resolve(window.pdfjsLib);
    }
    if (promoPdfJsPromise) return promoPdfJsPromise;

    promoPdfJsPromise = new Promise((resolve, reject) => {
      const trySource = (index) => {
        const source = promoPdfSources[index];
        if (!source) {
          reject(new Error('Unable to load the mobile PDF viewer'));
          return;
        }
        const script = document.createElement('script');
        script.src = source.src;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.dataset.promoPdfJs = 'true';
        script.onload = () => {
          if (window.pdfjsLib?.getDocument) {
            promoPdfWorkerSrc = source.worker;
            resolve(window.pdfjsLib);
            return;
          }
          script.remove();
          trySource(index + 1);
        };
        script.onerror = () => {
          script.remove();
          trySource(index + 1);
        };
        document.head.appendChild(script);
      };
      trySource(0);
    });
    return promoPdfJsPromise;
  }

  async function renderMobilePromoPdf() {
    const modal = document.querySelector('[data-promo-terms-modal]');
    const pdfRoot = modal?.querySelector('[data-promo-pdf]');
    const nativeViewer = pdfRoot?.querySelector('[data-promo-pdf-native]');
    const canvasViewer = pdfRoot?.querySelector('[data-promo-pdf-canvas]');
    const pdfUrl = pdfRoot?.dataset.promoPdfUrl;
    if (!modal || modal.hidden || !pdfRoot || !nativeViewer || !canvasViewer || !pdfUrl || !isMobilePromoTerms()) return;

    canvasViewer.hidden = false;
    canvasViewer.setAttribute('aria-busy', 'true');
    canvasViewer.innerHTML = '<div class="promo-terms-modal__pdf-canvas-loading">טוען את התקנון…</div>';
    nativeViewer.hidden = true;

    try {
      const pdfjs = await loadPromoPdfJs();
      if (pdfjs.GlobalWorkerOptions) pdfjs.GlobalWorkerOptions.workerSrc = promoPdfWorkerSrc;
      const pdf = await pdfjs.getDocument({ url: pdfUrl, withCredentials: false }).promise;
      const availableWidth = Math.max(120, Math.floor(canvasViewer.clientWidth - 12));
      const renderedWidth = Number(canvasViewer.dataset.renderedWidth || 0);
      if (renderedWidth === availableWidth && canvasViewer.children.length > 1) {
        canvasViewer.setAttribute('aria-busy', 'false');
        return;
      }

      const fragment = document.createDocumentFragment();
      const outputScale = Math.min(window.devicePixelRatio || 1, 2);
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = availableWidth / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const pageNode = document.createElement('div');
        pageNode.className = 'promo-terms-modal__pdf-page';
        pageNode.setAttribute('data-page-number', String(pageNumber));
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width * outputScale);
        canvas.height = Math.ceil(viewport.height * outputScale);
        canvas.style.width = `${Math.ceil(viewport.width)}px`;
        canvas.style.height = `${Math.ceil(viewport.height)}px`;
        pageNode.appendChild(canvas);
        fragment.appendChild(pageNode);
        await page.render({
          canvasContext: canvas.getContext('2d', { alpha: false }),
          viewport,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
        }).promise;
      }

      canvasViewer.replaceChildren(fragment);
      canvasViewer.dataset.renderedWidth = String(availableWidth);
      canvasViewer.setAttribute('aria-busy', 'false');
    } catch (error) {
      // Keep a usable fallback when a store CSP or a network policy blocks
      // the external PDF.js bundle or the PDF URL itself.
      console.warn('[Promo] Mobile PDF viewer fallback', error);
      canvasViewer.hidden = true;
      canvasViewer.removeAttribute('aria-busy');
      nativeViewer.hidden = false;
    }
  }

  window.addEventListener('resize', () => {
    const modal = document.querySelector('[data-promo-terms-modal]');
    if (!modal || modal.hidden || !isMobilePromoTerms()) return;
    window.clearTimeout(promoPdfResizeTimer);
    promoPdfResizeTimer = window.setTimeout(() => { void renderMobilePromoPdf(); }, 180);
  });

  function setTermsModalOpen(open) {
    const modal = document.querySelector('[data-promo-terms-modal]');
    if (!modal) return;
    modal.hidden = !open;
    modal.setAttribute('aria-hidden', String(!open));
    document.documentElement.classList.toggle('promo-terms-open', open);
    document.body?.classList.toggle('promo-terms-open', open);
    if (open) {
      modal.dispatchEvent(new CustomEvent('promo:terms-opened'));
      if (isMobilePromoTerms() && modal.querySelector('[data-promo-pdf]')) {
        window.setTimeout(() => { void renderMobilePromoPdf(); }, 0);
      }
    }
  }

  function openCartAfterChoice() {
    window.__promoChoicePending = false;
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      window.setTimeout(() => cartDrawer.open(), 80);
    }
  }

  function giftDisplayTitle(item) {
    const base = String(item.product_title || item.title || '').replace(/\s+-\s+Default Title$/i, '').trim();
    // Keep the selected model/color in private line-item properties for
    // variant matching and reporting, but show customers the simple product
    // title requested by the promotion UI (e.g. “LiberNovo StepSync
    // Footrest”, not the compatibility string).
    return base;
  }

  function currentPromoGiftLines(cart) {
    if (!productContext?.productId) return [];
    let parents = getParentLines(cart).filter((item) => Number(item.product_id) === Number(productContext.productId)
      && Number(item.variant_id) === Number(productContext.variantId));
    if (!parents.length) {
      parents = getParentLines(cart).filter((item) => Number(item.product_id) === Number(productContext.productId));
    }
    const parentKeys = new Set(parents.map((item) => item.key));
    const parentVariantIds = new Set(parents.map((item) => String(item.variant_id)));
    return cart.items.filter((item) => {
      if (!isPromoGift(item)) return false;
      const props = getProperties(item);
      // New lines carry the exact parent line key. Keep the variant-id
      // fallback for gifts created by an older engine version so the sticky
      // trigger can still reflect gifts that are visibly in the cart.
      return parentKeys.has(props._promo_parent_key)
        || (!props._promo_parent_key && parentVariantIds.has(String(props._promo_parent || '')));
    });
  }

  function promoGiftBundleReady(cart, gifts) {
    if (Date.now() < promoToastNotBefore) return false;
    const currentParents = getParentLines(cart).filter((item) => Number(item.product_id) === Number(productContext?.productId)
      && Number(item.variant_id) === Number(productContext?.variantId));
    const parentKeys = new Set(currentParents.map((item) => item.key));
    const expected = expectedGiftLines(cart).filter((line) => parentKeys.has(line.parentKey));
    if (!expected.length) return gifts.length > 0;
    return expected.every((line) => gifts.some((gift) => Number(gift.variant_id) === Number(line.variantId)
      && String(getProperties(gift)._promo_parent_key || '') === String(line.parentKey)
      && Number(gift.quantity || 0) >= Number(line.quantity || 1)));
  }

  function showPromoGiftToast(cart) {
    const gifts = currentPromoGiftLines(cart);
    if (!gifts.length || !promoGiftBundleReady(cart, gifts)) return false;

    let toast = document.querySelector('[data-promo-gift-toast]');
    if (!toast) {
      toast = document.createElement('aside');
      toast.className = 'promo-gift-toast';
      toast.dataset.promoGiftToast = 'true';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = `
        <div class="promo-gift-toast__topline">
          <strong class="promo-gift-toast__heading"></strong>
          <button type="button" class="promo-gift-toast__close" aria-label="סגירה">×</button>
        </div>
        <p class="promo-gift-toast__subtitle"></p>
        <div class="promo-gift-toast__items"></div>
        <button type="button" class="promo-gift-toast__cart-link">לצפייה בעגלה</button>`;
      document.body.append(toast);
      toast.querySelector('.promo-gift-toast__close')?.addEventListener('click', () => {
        toast.classList.remove('is-visible');
        window.setTimeout(() => toast.remove(), 220);
      });
      toast.querySelector('.promo-gift-toast__cart-link')?.addEventListener('click', () => {
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }
        // Fallback for themes that expose only the cart icon trigger.
        document.querySelector('[ref="cartDrawerButton"], [data-cart-drawer-toggle], cart-icon button, a[href="/cart"]')?.click();
      });
    }
    toast.querySelector('.promo-gift-toast__heading').textContent = 'המתנות שלך נוספו לעגלה';
    toast.querySelector('.promo-gift-toast__subtitle').textContent = 'מתנות במבצע';
    const items = toast.querySelector('.promo-gift-toast__items');
    items.replaceChildren(...gifts.slice(0, 5).map((gift) => {
      const item = document.createElement('div');
      item.className = 'promo-gift-toast__item';
      if (gift.image) {
        const image = document.createElement('img');
        image.src = gift.image;
        image.alt = '';
        image.loading = 'lazy';
        item.append(image);
      }
      const name = document.createElement('span');
      name.textContent = giftDisplayTitle(gift);
      item.append(name);
      return item;
    }));
    window.clearTimeout(promoToastTimer);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    promoToastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    }, 9000);
    return true;
  }

  function syncPromoGiftToastButton(cart) {
    const gifts = currentPromoGiftLines(cart);
    let trigger = document.querySelector('[data-promo-gift-toast-trigger]');
    if (!gifts.length) {
      if (trigger) trigger.hidden = true;
      return;
    }
    if (!trigger) {
      trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'promo-gift-toast-trigger';
      trigger.dataset.promoGiftToastTrigger = 'true';
      trigger.setAttribute('aria-label', 'מתנות שנוספו לעגלה');
      trigger.innerHTML = '<span aria-hidden="true">🎁</span><span>מתנות</span>';
      trigger.addEventListener('click', async () => {
        try {
          const latestCart = await getCart();
          syncPromoGiftToastButton(latestCart);
          promoToastNotBefore = 0;
          showPromoGiftToast(latestCart);
        } catch (_) {}
      });
      document.body.append(trigger);
    }
    trigger.hidden = false;
  }

  if (!document.getElementById('promo-gift-toast-styles')) {
    const style = document.createElement('style');
    style.id = 'promo-gift-toast-styles';
    style.textContent = `
      .promo-gift-toast { position: fixed; right: 24px; left: auto; bottom: 24px; z-index: 2147483000; width: min(380px, calc(100vw - 32px)); padding: 14px 16px 13px; border-top: 3px solid #f4512a; background: #fff; color: #212d3b; box-shadow: 0 12px 35px rgba(20,30,42,.24); direction: rtl; opacity: 0; transform: translateY(16px); pointer-events: none; transition: opacity .2s ease, transform .2s ease; }
      .promo-gift-toast.is-visible { opacity: 1; transform: translateY(0); pointer-events: auto; }
      .promo-gift-toast__topline { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
      .promo-gift-toast__heading { font-size: 14px; }
      .promo-gift-toast__close { border: 0; padding: 0; background: transparent; color: #9aa3ab; font-size: 20px; line-height: 1; cursor: pointer; }
      .promo-gift-toast__subtitle { margin: 2px 0 9px; color: #7b8794; font-size: 12px; }
      .promo-gift-toast__items { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-height: 44px; }
      .promo-gift-toast__item { display: inline-flex; align-items: center; gap: 6px; max-width: 100%; color: #36424e; font-size: 11px; font-weight: 700; }
      .promo-gift-toast__item img { width: 42px; height: 42px; object-fit: contain; border: 1px solid #e1e4e6; background: #fff; }
      .promo-gift-toast__cart-link { display: inline-block; margin-top: 10px; padding: 0; border: 0; background: transparent; color: #212d3b; font: inherit; font-size: 12px; font-weight: 700; text-decoration: underline; cursor: pointer; }
      .promo-gift-toast-trigger { position: fixed; top: auto; right: 0; bottom: 132px; z-index: 2147482000; display: inline-flex; align-items: center; gap: 5px; padding: 10px 8px; border: 0; border-radius: 8px 0 0 8px; background: #212d3b; color: #fff; box-shadow: 0 5px 18px rgba(20,30,42,.24); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
      .promo-gift-toast-trigger span:first-child { color: #f4512a; font-size: 16px; }
      .promo-gift-toast-trigger[hidden] { display: none; }
      @media (max-width: 749px) { .promo-gift-toast { right: 16px; left: 16px; bottom: 16px; width: auto; } .promo-gift-toast-trigger { top: auto; right: 0; bottom: 118px; } }
    `;
    document.head.append(style);
  }

  async function updateChoiceModalAttached(eventOrCart) {
    const modal = getChoiceModal();
    const attached = modal?.querySelector('[data-promo-choice-attached]');
    const attachedItems = modal?.querySelector('[data-promo-choice-attached-items]');
    const attachedCopy = modal?.querySelector('.promo-choice-modal__attached-copy');
    const text = modal?.querySelector('[data-promo-choice-attached-text]');
    const progress = modal?.querySelector('[data-promo-choice-progress]');
    const progressStepsContainer = modal?.querySelector('[data-promo-choice-steps]');
    const progressLabel = modal?.querySelector('[data-promo-choice-progress-label]');
    const instruction = modal?.querySelector('[data-promo-choice-instruction]');
    const confirm = modal?.querySelector('[data-promo-choice-confirm]');
    if (!modal || !attached || !text || !productContext?.choiceEnabled) return;
    try {
      // Reconciliation already has a fresh cart snapshot. Reuse it when this
      // function is called by promo:cart-reconciled instead of requesting
      // /cart.js again. Direct customer interactions can still fetch on demand.
      const suppliedCart = Array.isArray(eventOrCart?.items)
        ? eventOrCart
        : eventOrCart?.detail?.cart;
      const cart = suppliedCart && Array.isArray(suppliedCart.items)
        ? suppliedCart
        : await getCart();
      const eligibleParents = cart.items.filter((item) => {
        return propertyIsTrue(item, '_promo_eligible') && !isPromoGift(item)
          && (!productContext?.productId || Number(item.product_id) === Number(productContext.productId));
      });
      const exactParents = eligibleParents.filter((item) => Number(item.variant_id) === Number(productContext.variantId));
      const parents = exactParents.length ? exactParents : (eligibleParents.length === 1 ? eligibleParents : []);
      const parentKeys = new Set(parents.map((item) => item.key));
      const gifts = cart.items.filter((item) => parentKeys.has(getProperties(item)._promo_parent_key));
      const choiceGifts = gifts.filter((item) => getProperties(item)._promo_gift_type === 'choice');
      const pendingChoice = readPendingChoice();
      const pendingAlreadyAttached = pendingChoice && gifts.some((item) => Number(item.variant_id) === Number(pendingChoice.variantId)
        && getProperties(item)._promo_gift_type === 'choice');
      const pendingChoiceItem = pendingChoice && !pendingAlreadyAttached ? {
        variant_id: Number(pendingChoice.variantId),
        product_title: pendingChoice.productTitle || pendingChoice.title || '',
        title: pendingChoice.title || '',
        image: pendingChoice.image || '',
      } : null;
      const totalChoices = Math.min(parents.reduce((total, item) => total + Number(item.quantity || 0), 0), config.maxUnits);
      const chosenChoices = choiceGifts.reduce((total, item) => total + Number(item.quantity || 0), 0);
      const nextChoice = Math.min(chosenChoices + 1, totalChoices);
      const selectedStep = Math.min(
        Math.max(Number(modal.dataset.promoChoiceStep || nextChoice), 1),
        Math.max(totalChoices, 1)
      );

      if (progress) {
        if (progressStepsContainer) {
          progressStepsContainer.replaceChildren();
          for (let stepNumber = 1; stepNumber <= totalChoices; stepNumber += 1) {
            const step = document.createElement('button');
            step.type = 'button';
            step.className = 'promo-choice-modal__step';
            step.dataset.promoChoiceStep = String(stepNumber);
            step.setAttribute('aria-label', `מתנה ${stepNumber}`);
            step.disabled = stepNumber > nextChoice;
            step.classList.toggle('is-current', stepNumber === selectedStep);
            step.classList.toggle('is-complete', stepNumber <= chosenChoices);
            step.setAttribute('aria-current', stepNumber === selectedStep ? 'step' : 'false');
            progressStepsContainer.append(step);
          }
        }
        if (progressLabel) {
          progressLabel.textContent = totalChoices > 1 && chosenChoices < totalChoices
            ? `מתנה ${selectedStep} מתוך ${totalChoices}`
            : '';
        }
        progress.hidden = !(totalChoices > 1 && chosenChoices < totalChoices);
      }
      if (confirm) {
        const selected = Boolean(modal.querySelector('[data-promo-choice-item].is-selected'));
        // A customer can preselect one choice before adding the chair. In
        // that state there is no parent line yet, so totalChoices is zero but
        // the confirmation button must still be usable.
        const replacingExistingChoice = modal.dataset.promoChoiceReplace === 'true';
        const hasMoreChoices = replacingExistingChoice
          || !parents.length
          || (totalChoices > 0 && chosenChoices < totalChoices);
        confirm.disabled = !hasMoreChoices || !selected;
        if (hasMoreChoices && selected && totalChoices > 1 && nextChoice < totalChoices) {
          confirm.textContent = `אשר והמשך למתנה ${nextChoice + 1} מתוך ${totalChoices}`;
        } else {
          confirm.textContent = 'אשר את הבחירה';
        }
      }
      if (instruction && !modal.querySelector('[data-promo-choice-item].is-selected')) {
        instruction.textContent = 'בחר אביזר אחד מהרשימה';
      }

      // The strip is a compact list of all gifts already included with this
      // chair. Before the chair reaches the cart, use the product context's
      // exact configured variants so its automatic Footrest/Mouse Pad/Warranty
      // thumbnails are still visible. Do not render names in this strip.
      const previewAutomaticItems = resolvedGiftDetails(productContext)
        .filter((gift) => gift.kind !== 'choice')
        .map((gift) => ({
          variant_id: Number(gift.id || 0),
          product_title: gift.productTitle || gift.title || '',
          title: gift.title || '',
          image: gift.image || '',
        }));
      const attachedGiftItems = [
        ...previewAutomaticItems,
        ...gifts.filter((item) => getProperties(item)._promo_gift_type !== 'choice'),
        ...choiceGifts,
        ...(pendingChoiceItem ? [pendingChoiceItem] : []),
      ].filter((item, index, list) => {
        const variantId = Number(item.variant_id || item.id || 0);
        return variantId > 0 && list.findIndex((candidate) => Number(candidate.variant_id || candidate.id || 0) === variantId) === index;
      });
      if (!attachedGiftItems.length) {
        if (attachedItems) attachedItems.replaceChildren();
        attached.hidden = true;
        return;
      }

      if (attachedItems) {
        attachedItems.replaceChildren();
        attachedGiftItems.forEach((item) => {
          const wrapper = document.createElement('span');
          wrapper.className = 'promo-choice-modal__attached-item';
          wrapper.title = giftDisplayTitle(item);

          const tick = document.createElement('span');
          tick.className = 'promo-choice-modal__tick';
          tick.textContent = '✓';
          if (item.image) {
            const image = document.createElement('img');
            image.src = item.image;
            image.alt = '';
            image.loading = 'lazy';
            wrapper.append(image);
          }
          wrapper.append(tick);
          attachedItems.append(wrapper);
        });
      }

      // The client requested only the selected-gift image and a concise
      // Hebrew confirmation—no product-name list in this popup strip.
      text.textContent = '';
      if (attachedCopy) attachedCopy.textContent = 'המתנות שנבחרו';
      attached.hidden = false;
    } catch (error) {
      if (attachedItems) attachedItems.replaceChildren();
      attached.hidden = true;
    }
  }

  async function addChoiceGift(button, modal) {
    if (choiceSelectionInFlight) return;
    choiceSelectionInFlight = true;
    // Capture this before any async cart work. The modal can be refreshed by
    // reconciliation while the request is in flight; the user's intent to
    // replace an already-selected gift must survive those refreshes.
    const replaceSelectedChoice = modal.dataset.promoChoiceReplace === 'true';
    const confirm = modal.querySelector('[data-promo-choice-confirm]');
    if (confirm) { confirm.disabled = true; confirm.setAttribute('aria-busy', 'true'); }
    const choiceModel = String(button.dataset.productModel || '').trim();
    const choiceColor = String(button.dataset.productColor || '').trim();
    const choiceDisplayVariant = choiceModel && choiceColor && choiceModel.toLowerCase() !== choiceColor.toLowerCase()
      ? `${choiceModel} / ${choiceColor}`
      : (choiceModel || choiceColor);
    const choice = {
      variantId: Number(button.dataset.variantId),
      productTitle: button.dataset.productTitle || '',
      title: button.dataset.productTitle || '',
      variantTitle: choiceDisplayVariant,
      model: choiceModel,
      color: choiceColor,
      price: Number(button.dataset.variantPrice || 0),
      image: button.querySelector('img')?.currentSrc || button.querySelector('img')?.src || '',
    };
    let cart;
    try {
      cart = await getCart();
    } catch (error) {
      choiceSelectionInFlight = false;
      if (confirm) { confirm.disabled = false; confirm.removeAttribute('aria-busy'); }
      throw error;
    }
    const eligibleParents = cart.items.filter((item) => {
      const props = getProperties(item);
      return propertyIsTrue(item, '_promo_eligible')
        && !isPromoGift(item)
        && (!productContext?.productId || Number(item.product_id) === Number(productContext.productId));
    });
    const currentModel = modelKey([productContext?.chairModel, productContext?.handle].filter(Boolean).join(' '));
    const currentColor = colorKey(productContext?.chairColor || '');
    const parent = eligibleParents
      .map((item, index) => {
        const props = getProperties(item);
        const parentModel = modelKey(props._promo_chair_model || item.product_title || item.title || '');
        const parentColor = colorKey(props._promo_chair_color || '');
        let score = 0;
        if (Number(item.variant_id) === Number(productContext.variantId)) score += 1000;
        if (currentModel && parentModel === currentModel) score += 200;
        if (currentColor && parentColor === currentColor) score += 100;
        // Preserve the cart's existing order as a deterministic final tie
        // breaker when more than one eligible chair is present.
        score += Math.max(0, eligibleParents.length - index);
        return { item, score };
      })
      .sort((a, b) => b.score - a.score)[0]?.item || null;
    if (!parent) {
      // No chair yet: remember the selection in this browser session and
      // render it in the selector's attached-gifts area, but do not sell it
      // as a standalone cart item. The selection is attached automatically
      // when this chair is added later.
      writePendingChoice(choice, { status: 'preselected' });
      modal.querySelectorAll('[data-promo-choice-item]').forEach((item) => item.classList.remove('is-selected'));
      choiceSelectionInFlight = false;
      if (confirm) { confirm.disabled = false; confirm.removeAttribute('aria-busy'); }
      setChoiceModalOpen(false);
      document.dispatchEvent(new CustomEvent('promo:pending-choice-updated', { detail: { choice } }));
      return;
    }
    clearDismissedGift(parent.key, choice.variantId);
    writePendingChoice(choice, { status: 'attaching', parentKey: parent.key });
    document.dispatchEvent(new CustomEvent('promo:pending-choice-updated', { detail: { choice } }));
    promoToastPending = true;
    promoToastRetryCount = 0;
    promoToastNotBefore = Date.now() + 350;
    window.clearTimeout(promoToastRetryTimer);
    try {
      const parentChoiceLines = cart.items.filter((item) => {
        const props = getProperties(item);
        return props._promo_parent_key === parent.key && props._promo_gift_type === 'choice';
      });
      const totalChoices = Math.min(Number(parent.quantity || 0), config.maxUnits);
      const chosenChoices = parentChoiceLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      // When the customer opens an already-selected gift tile, replace one
      // existing choice instead of stacking a second free gift. For a
      // multi-chair order this defaults to replacing after all slots are full.
      const shouldReplace = replaceSelectedChoice
        || (totalChoices > 0 && chosenChoices >= totalChoices);
      if (shouldReplace && parentChoiceLines.length) {
        // Remove every existing choice line before adding the replacement.
        // This keeps replacement idempotent even if an earlier request left
        // duplicate choice lines in the cart.
        await updateCartQuantities(Object.fromEntries(
          parentChoiceLines.map((item) => [item.key, 0]),
        ));
      }
      const response = await nativeFetch(config.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(button.dataset.variantId),
            quantity: 1,
            properties: {
              _promo_gift: 'true',
              _promo_campaign: config.campaign,
              _promo_parent: String(parent.variant_id),
              _promo_parent_key: parent.key,
              _promo_gift_type: 'choice',
              _promo_gift_variant_title: button.dataset.productTitle || '',
              _promo_gift_model: button.dataset.productModel || '',
              _promo_gift_color: button.dataset.productColor || '',
              _promo_gift_display_variant: choiceDisplayVariant,
              _promo_parent_model: String(productContext.chairModel || ''),
              _promo_parent_color: String(productContext.chairColor || ''),
              ...couponStackingProperties(),
            },
          }],
        }),
      });
      if (!response.ok) throw new Error('Choice gift add failed');

      // Keep the parent's serialized bundle in sync when a customer replaces a
      // choice (or selects one after the chair is already in the cart). Without
      // this, reconciliation reads the old choice from _promo_bundle and adds
      // it back immediately after the new choice is selected.
      let nextBundle = [];
      let nextBundleMeta = [];
      try { nextBundle = JSON.parse(getProperties(parent)._promo_bundle || '[]'); } catch (_) { nextBundle = []; }
      try { nextBundleMeta = JSON.parse(getProperties(parent)._promo_bundle_meta || '[]'); } catch (_) { nextBundleMeta = []; }
      if (!Array.isArray(nextBundle)) nextBundle = [];
      if (!Array.isArray(nextBundleMeta)) nextBundleMeta = [];
      const replacedChoiceIds = new Set(parentChoiceLines.map((item) => Number(item.variant_id)).filter(Boolean));
      if (shouldReplace && replacedChoiceIds.size) {
        nextBundle = nextBundle.filter((variantId) => !replacedChoiceIds.has(Number(variantId)));
        nextBundleMeta = nextBundleMeta.filter((gift) => !replacedChoiceIds.has(Number(gift?.id)));
      }
      const selectedVariantId = Number(choice.variantId || 0);
      if (selectedVariantId && !nextBundle.some((variantId) => Number(variantId) === selectedVariantId)) {
        nextBundle.push(selectedVariantId);
        nextBundleMeta.push({
          id: selectedVariantId,
          productTitle: choice.productTitle || '',
          title: choice.title || choice.productTitle || '',
          variantTitle: choice.variantTitle || '',
          model: choice.model || '',
          color: choice.color || '',
          price: choice.price || 0,
          kind: 'choice',
        });
      }
      await changeLine(parent.key, parent.quantity, {
        ...getProperties(parent),
        _promo_bundle: JSON.stringify(nextBundle),
        _promo_bundle_meta: JSON.stringify(nextBundleMeta),
      });
      // Changing the parent properties can give the chair line a new Shopify
      // line key. Re-point the newly added (and any remaining) choice gift
      // lines before reconciliation, otherwise they look orphaned and are
      // removed immediately because they still reference the old key.
      const parentAfterChange = await getCart();
      const updatedParent = parentAfterChange.items.find((item) =>
        propertyIsTrue(item, '_promo_eligible')
        && !isPromoGift(item)
        && Number(item.product_id) === Number(parent.product_id)
        && Number(item.variant_id) === Number(parent.variant_id)
        && (!getProperties(parent)._bundle_uuid
          || getProperties(item)._bundle_uuid === getProperties(parent)._bundle_uuid)
      );
      if (updatedParent && updatedParent.key !== parent.key) {
        const choiceLinesToRepoint = parentAfterChange.items.filter((item) => {
          const props = getProperties(item);
          return props._promo_parent_key === parent.key && props._promo_gift_type === 'choice';
        });
        await Promise.all(choiceLinesToRepoint.map((item) => changeLine(item.key, item.quantity, {
          ...getProperties(item),
          _promo_parent_key: updatedParent.key,
        })));
      }
    } finally {
      choiceSelectionInFlight = false;
      modal.dataset.promoChoiceReplace = 'false';
    }
    await refreshCartDrawer();
    modal.querySelectorAll('[data-promo-choice-item]').forEach((item) => item.classList.remove('is-selected'));
    scheduleReconcile();
    const updatedCart = await getCart();
    // The choice was written before the request so the tile never falls back
    // to “+ choose”. Once the server confirms the selected line is attached,
    // clear that temporary value; the cart line itself is now authoritative.
    const attachedChoice = updatedCart.items.some((item) => {
      const props = getProperties(item);
      return Number(item.variant_id) === Number(choice.variantId)
        && props._promo_gift_type === 'choice'
        && (props._promo_parent_key || props._promo_parent);
    });
    if (attachedChoice) {
      clearPendingChoice();
      document.dispatchEvent(new CustomEvent('promo:pending-choice-updated'));
    }
    const parents = updatedCart.items.filter((item) => propertyIsTrue(item, '_promo_eligible') && !isPromoGift(item)
      && Number(item.variant_id) === Number(productContext.variantId));
    const parentKeys = new Set(parents.map((item) => item.key));
    const chosen = updatedCart.items
      .filter((item) => parentKeys.has(getProperties(item)._promo_parent_key)
        && getProperties(item)._promo_gift_type === 'choice')
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    const total = Math.min(parents.reduce((sum, item) => sum + Number(item.quantity || 0), 0), config.maxUnits);
    if (chosen < total) {
      modal.dataset.promoChoiceStep = String(Math.min(chosen + 1, total));
      setChoiceModalOpen(true);
      await updateChoiceModalAttached();
    } else {
      setChoiceModalOpen(false);
      openCartAfterChoice();
    }
  }

  function initChoiceModal() {
    const modal = getChoiceModal();
    if (!modal || modal.dataset.initialized === 'true') return;
    modal.dataset.initialized = 'true';

    const selectChoiceItem = (item) => {
      if (!item || item.disabled) return;
      modal.querySelectorAll('[data-promo-choice-item]').forEach((choice) => {
        const selected = choice === item;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      modal.dataset.promoChoiceStep = modal.querySelector('[data-promo-choice-step].is-current')?.dataset.promoChoiceStep || '1';
      const instruction = modal.querySelector('[data-promo-choice-instruction]');
      if (instruction) instruction.textContent = item.dataset.productTitle || 'בחר אביזר אחד מהרשימה';
      const confirm = modal.querySelector('[data-promo-choice-confirm]');
      if (confirm) {
        confirm.disabled = false;
        confirm.removeAttribute('aria-disabled');
        confirm.onclick = (event) => {
          event.preventDefault();
          addChoiceGift(item, modal).catch((error) => console.warn('[Promo] Choice gift skipped', error));
        };
      }
      void updateChoiceModalAttached();
    };

    // Bind directly to the cards as well as using delegation. This keeps card
    // selection reliable when the theme's global click handlers intercept clicks.
    modal.querySelectorAll('[data-promo-choice-item]').forEach((item) => {
      item.setAttribute('aria-pressed', 'false');
      item.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectChoiceItem(item);
      }, true);
      item.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectChoiceItem(item);
      }, true);
    });

    modal.addEventListener('click', (event) => {
      const close = event.target.closest('[data-promo-choice-close]');
      if (close) {
        window.__promoChoicePending = false;
        setChoiceModalOpen(false);
      }
      const item = event.target.closest('[data-promo-choice-item]');
      if (item) {
        event.preventDefault();
        event.stopPropagation();
        selectChoiceItem(item);
      }
      const step = event.target.closest('[data-promo-choice-step]');
      if (step && !step.disabled) {
        event.preventDefault();
        event.stopPropagation();
        modal.dataset.promoChoiceStep = step.dataset.promoChoiceStep || '1';
        modal.querySelectorAll('[data-promo-choice-item]').forEach((choice) => choice.classList.remove('is-selected'));
        const instruction = modal.querySelector('[data-promo-choice-instruction]');
        if (instruction) instruction.textContent = 'בחר אביזר אחד מהרשימה';
        const confirm = modal.querySelector('[data-promo-choice-confirm]');
        if (confirm) {
          confirm.disabled = true;
          confirm.textContent = 'אשר את הבחירה';
          confirm.onclick = null;
        }
        updateChoiceModalAttached();
      }
    });
    document.addEventListener('promo:choice-open', (event) => {
      // Set the replacement mode before refreshing cards. Refresh/reconcile
      // can synchronously touch the modal and must not erase this intent.
      modal.dataset.promoChoiceReplace = event.detail?.replace ? 'true' : 'false';
      refreshChoiceVariantCards();
      setChoiceModalOpen(true);
    });
    document.addEventListener('promo:choice-open', updateChoiceModalAttached);
    document.addEventListener('promo:context-updated', refreshChoiceVariantCards);
    document.addEventListener('promo:pending-choice-updated', updateChoiceModalAttached);
    document.addEventListener('promo:choice-pending', () => {
      if (!productContext?.choiceEnabled) return;
      if (Date.now() < promoCartMutationUntil) return;
      setChoiceModalOpen(true);
      updateChoiceModalAttached();
    });
    document.addEventListener('promo:cart-reconciled', updateChoiceModalAttached);
    const reminderLink = document.querySelector('[data-promo-choice-reminder-link]');
    reminderLink?.addEventListener('click', (event) => {
      event.preventDefault();
      setChoiceModalOpen(true);
      updateChoiceModalAttached();
    });
    refreshChoiceVariantCards();
    if (productContext?.choiceEnabled && new URLSearchParams(window.location.search).get('promo_choose') === '1') {
      window.setTimeout(() => setChoiceModalOpen(true), 350);
    }
  }

  // The summary block can be rendered after the engine's initialisation (for
  // example after a product-context swap). Use delegated handling so its
  // “choose gift” button always opens the same modal.
  document.addEventListener('click', (event) => {
    const termsOpen = event.target.closest?.('[data-promo-terms-open]');
    if (termsOpen) {
      event.preventDefault();
      event.stopPropagation();
      setTermsModalOpen(true);
      return;
    }
    const termsClose = event.target.closest?.('[data-promo-terms-close]');
    if (termsClose) {
      event.preventDefault();
      event.stopPropagation();
      setTermsModalOpen(false);
      return;
    }
    const button = event.target.closest?.('[data-promo-summary-choice]');
    if (!button || !productContext?.choiceEnabled) return;
    event.preventDefault();
    event.stopPropagation();
    initChoiceModal();
    const modal = getChoiceModal();
    if (modal) modal.dataset.promoChoiceReplace = button.dataset.promoChoiceSelected === 'true' ? 'true' : 'false';
    refreshChoiceVariantCards();
    setChoiceModalOpen(true);
    void updateChoiceModalAttached();
  }, true);

  // Some mobile browsers do not consistently synthesize a click after a
  // touch on a button inside a fixed overlay. Handle the pointer release as
  // a fallback so the terms popup still opens/closes reliably. The normal
  // click handler remains in place for keyboard and mouse users.
  document.addEventListener('pointerup', (event) => {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    const termsOpen = event.target.closest?.('[data-promo-terms-open]');
    const termsClose = event.target.closest?.('[data-promo-terms-close]');
    if (!termsOpen && !termsClose) return;
    event.preventDefault();
    event.stopPropagation();
    setTermsModalOpen(Boolean(termsOpen));
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setTermsModalOpen(false);
  });

  function hasParentForChoice(item, cart) {
    const props = getProperties(item);
    return cart.items.some((parent) => parent.key === props._promo_parent_key && propertyIsTrue(parent, '_promo_eligible') && !isPromoGift(parent));
  }

  function rememberDismissedGiftFromControl(control, quantityInputChanged = false) {
    // The redesigned cart uses a custom gift row, not Shopify's native cart
    // row. Store the exact gift identity directly on its remove button before
    // the cart line is deleted, so reconciliation cannot add it back.
    if (control?.matches?.('[data-promo-dismiss-gift]')) {
      dismissGift(
        control.dataset.promoParentKey,
        Number(control.dataset.promoGiftVariantId || 0),
        control.dataset.promoGiftType || 'auto',
        Number(control.dataset.promoGiftQuantity || 1),
      );
      return;
    }

    const row = control?.closest?.('.cart-items__table-row, .newton-cart-gift-row');
    if (!row || row.dataset.promoGift !== 'true') return;

    const isRemoveButton = control.matches?.('[data-cart-remove], cart-remove-button, .cart-items__remove');
    const isLastMinus = control.matches?.(
      'quantity-selector-component button[name="minus"], cart-items-component button[name="minus"]',
    ) && Number(control.closest('quantity-selector-component')?.querySelector('input')?.value || 0) <= 1;
    if (!isRemoveButton && !isLastMinus && !quantityInputChanged) return;

    dismissGift(
      row.dataset.promoParentKey,
      Number(row.dataset.promoGiftVariantId || 0),
      row.dataset.promoGiftType || 'auto',
      Number(control.closest('quantity-selector-component')?.querySelector('input')?.value || 1),
    );
  }

  let cartMutationTimer;
  const cartMutationObserver = new MutationObserver((records) => {
    if (Date.now() < promoDrawerRefreshUntil) return;
    const cartChanged = records.some((record) => {
      const target = record.target instanceof Element ? record.target : null;
      // Count/badge text can live inside the cart drawer. It is updated by
      // refreshCartBubble(), so treating that UI-only mutation as a cart item
      // change creates a reconcile -> count mutation -> reconcile loop.
      if (target?.closest('cart-icon, .cart-bubble, .cart-bubble__text-count, .cart-count-bubble, .cart-count, [ref="cartBubbleCount"], [data-cart-count]')) {
        return false;
      }

      // Actual cart mutations are covered by drawer/item structure changes,
      // cart events, request hooks, and delegated cart-control handlers.
      return target?.closest('cart-drawer-component, cart-items-component')
        || [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element
          && node.closest('cart-drawer-component, cart-items-component'));
    });
    if (!cartChanged) return;
    window.clearTimeout(cartMutationTimer);
    cartMutationTimer = window.setTimeout(scheduleReconcile, 250);
  });
  cartMutationObserver.observe(document.body, { childList: true, subtree: true });

  // Some theme cart controls update through custom elements and do not emit a
  // reliable cart event. Start reconciliation as soon as a remove/quantity
  // control is clicked, then the fetch/XHR hooks will run it again after the
  // server has committed the cart change.
  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-cart-remove], [data-cart-quantity], cart-remove-button, .cart-items__remove, quantity-selector-component button[name="minus"], quantity-selector-component button[name="plus"], cart-items-component button[name="minus"], cart-items-component button[name="plus"]');
    if (!control) return;
    rememberDismissedGiftFromControl(control);
    // A cart remove/quantity click is not a request to choose a gift. Block
    // transient pending-choice events until the drawer mutation has settled.
    promoCartMutationUntil = Math.max(promoCartMutationUntil, Date.now() + 3000);
    setChoiceModalOpen(false);
    pendingChoiceState = false;
    window.__promoChoicePending = false;
    window.setTimeout(scheduleReconcile, 350);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('quantity-selector-component input');
    if (!input || Number(input.value || 0) > 0) return;
    rememberDismissedGiftFromControl(input, true);
  }, true);

  window.addEventListener('libernovo:product-page-swapped', (event) => {
    // The product switcher replaces #MainContent, including the promo JSON,
    // modal and add-to-cart form. Read the new context before the next add.
    readProductContext();
    // The switcher knows the selected colour even before the native
    // variant:change event is emitted. Apply it immediately so the summary
    // preview cannot briefly keep the previous colour's gift image.
    syncPromoProductVariant(event.detail || {});
    document.documentElement.classList.remove('promo-choice-open');
    initChoiceModal();
    initPromoCountdowns();
    scheduleReconcile();
  });

  document.addEventListener('cart:updated', scheduleReconcile);
  document.addEventListener('cart:update', scheduleReconcile);
  const startPromoEngine = () => {
    initPromoCountdowns();
    initChoiceModal();
    // reconcile() performs the initial cart read and all stale-choice/toast
    // synchronization. A separate getCart() here duplicated every page load.
    scheduleReconcile();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPromoEngine, { once: true });
  } else {
    // A product section can be loaded after DOMContentLoaded, so do not wait
    // for an event that has already fired.
    startPromoEngine();
  }
  initChoiceModal();
  initPromoCountdowns();
})();
